import { scrollPageIntoView } from '../pageScroll';
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useDrag, useDrop, useDragLayer } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { PlusIcon, ArrowPathIcon, CheckIcon, ChatBubbleBottomCenterTextIcon, FireIcon, LinkIcon, XMarkIcon, MapPinIcon, ChevronDownIcon, Bars3Icon } from '@heroicons/react/24/solid';
import './Workout.css';
import './WorkoutProposalBubble.css';
import { IDLE_COMPLETION, SUBMIT_NOUNS, SubmitFeedbackAnnouncement, SubmitFeedbackButton } from './SubmitFeedback';
import { useFeature } from '../../contexts/SubscriptionContext';
import useWorkoutRecommendationJob from './useWorkoutRecommendationJob';
import { isWorkoutEditable as _isWorkoutEditable, isWorkoutExpired } from '../../utils/workoutUtils';
import { formatTravelReplacementReason } from '../../utils/travelReplacementUtils';
import { estimateSetCalories } from '../../utils/volume';
import WorkoutExercise from './WorkoutExercise';
import { isSelectionTap } from './workoutExerciseGesture';
import { getInitialWorkoutExercises } from './sharedWorkoutReadOnlyData';
import SupersetExercise from './SupersetExercise';
import WorkoutCoachDock from './WorkoutCoachDock';
import FloatingSelectionActions from '../FloatingSelectionActions';
import WorkoutStreamViewer from './WorkoutStreamViewer';
import { shouldShowWorkoutCoachDock } from './workoutCoachThreadPresentation';
import { buildWorkoutProposalAnnotations } from './workoutProposalAnnotations';
import { createWorkoutCoachRefreshScheduler } from './workoutCoachRefreshScheduler';
import { useDaySwapEligibility } from './workoutDayActionEligibility';
import ExerciseSelectionModal from './ExerciseSelectionModal';
import RegenerateWorkoutModal from '../RegenerateWorkoutModal';
import TravelReplacementModal from '../TravelReplacementModal';
import GymSelector from './GymSelector';
import { useGeolocationService } from '../../contexts/GeolocationServiceContext';
import { isForeground, subscribeForeground } from '../../services/AppStateService';
import DiscordPresenceService from '../../services/DiscordPresenceService';
import { workoutExerciseScopeKey } from '../../services/workoutSetScope';
import { coachSequenceNow, noteCoachCanonicalRead, noteCoachContextMatch } from '../../services/coachSequenceTiming';
import {
  hideResolvedCoachActions,
  mergeExerciseRecommendation,
  replaceExerciseRecommendations,
} from './exerciseCoachSuggestionState';
import { buildCoachAppliedTargets, reconcileCoachAppliedTargets, useCoachAppliedFieldEffects } from './coachAppliedFieldEffect';
import { buildCoachAddedSetTargets, useCoachAddedSetEffects } from './coachAddedSetEffect';

// Helper to get YYYY-MM-DD date key; used for multipart workout filtering
const getDateKey = (date, timezone) => {
  try {
    return new Date(date).toLocaleDateString('en-CA', { timeZone: timezone || undefined });
  } catch {
    return new Date(date).toISOString().split('T')[0];
  }
};

const ItemType = {
  WORKOUT_EXERCISE: 'workoutExercise',
};

const selectionDebugEnabled = () => {
  try {
    const search = typeof window !== 'undefined' ? (window.location?.search || '') : '';
    if (search && /[?&]debugSelection=(1|true|yes|on)\b/i.test(search)) return true;
    if (typeof window !== 'undefined' && window.__debugSelection) return true;

    const raw = String((typeof window !== 'undefined' && window.localStorage)
      ? (window.localStorage.getItem('debugSelection') || '')
      : ''
    ).trim().toLowerCase();
    return raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on';
  } catch (_) {
    // localStorage may be blocked in some browser privacy modes
    return Boolean(typeof window !== 'undefined' && window.__debugSelection);
  }
};

const selectionLog = (...args) => {
  if (!selectionDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.log('[SelectionDebug]', ...args);
};

const isInteractiveElement = (node) => {
  try {
    if (!node || typeof node.closest !== 'function') return false;
    return Boolean(node.closest('button, input, select, textarea, a, label, [contenteditable="true"], [contenteditable=""], [role="button"], [role="link"]'));
  } catch (_) {
    return false;
  }
};

const centerStabilizers = new WeakMap();

const stabilizeCenteredScroll = (element, durationMs = 1200) => {
  if (!element) return;

  const prevCancel = centerStabilizers.get(element);
  if (typeof prevCancel === 'function') {
    prevCancel();
  }

  let canceled = false;
  let observer = null;
  let rafId = null;
  let timeoutId = null;
  let intervalId = null;

  const cleanup = () => {
    if (observer) observer.disconnect();
    observer = null;
    if (rafId) cancelAnimationFrame(rafId);
    rafId = null;
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = null;
    if (intervalId) clearInterval(intervalId);
    intervalId = null;
    window.removeEventListener('wheel', cancel, true);
    window.removeEventListener('touchmove', cancel, true);
    window.removeEventListener('keydown', cancel, true);
  };

  const doCenter = () => {
    if (canceled) return;
    try {
      scrollPageIntoView(element, { behavior: 'auto', block: 'center', inline: 'nearest' });
    } catch (_) {
      scrollPageIntoView(element);
    }
  };

  const scheduleCenter = () => {
    if (canceled) return;
    if (rafId) return;
    rafId = requestAnimationFrame(() => {
      rafId = null;
      doCenter();
    });
  };

  function cancel() {
    if (canceled) return;
    canceled = true;
    cleanup();
  }

  centerStabilizers.set(element, cancel);

  // If the user scrolls/keys during the animation window, respect that and stop fighting it.
  window.addEventListener('wheel', cancel, true);
  window.addEventListener('touchmove', cancel, true);
  window.addEventListener('keydown', cancel, true);

  // Initial center, then keep centered while the element resizes (e.g., chart animating).
  scheduleCenter();

  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(() => {
      scheduleCenter();
    });
    observer.observe(element);
  } else {
    // Fallback for older browsers: periodic re-center for the short window.
    intervalId = setInterval(() => scheduleCenter(), 100);
  }

  timeoutId = setTimeout(cancel, durationMs);
};

const DraggableWorkoutExercise = ({
  workout,
  workout_exercise,
  index,
  moveWorkoutExercise,
  onDragEnd,
  exercises,
  backendService,
  modalService,
  refreshWorkout,
  onCoachSuggestionResolved,
  isOngoing,
  setOngoingExerciseId,
  isComplianceCritical,
  recommendation,
  isLoadingRecommendation,
  isUpdatingRecommendation,
  onSubmitMessage,
  onAnswerQuestion,
  coachImageUrl = null,
  coachEmoji = '🥋',
  showInlineCoach = true,
  proposalAnnotations = [],
  onApproveProposal,
  busyStrategyProposalId,
  coachAddedSetEffects,
  onCoachAddedSetDisplayed,
  coachFieldEffects,
  onCoachFieldEffectDisplayed,
  onCompletedSetFieldChanged,
  isSelectionMode = false,
  selectionOrder,
  readOnly = false,
  structureLocked = false,
  allowValueEditingWhenStructureLocked = false,
  setDateFilter
}) => {
  const ref = useRef(null);
  const [isLongPress, setIsLongPress] = useState(false);
  const longPressTimeout = useRef(null);
  const userInitiatedScrollRef = useRef(false);
  const selectionPointerStartRef = useRef(null);

  const [, drop] = useDrop({
    accept: ItemType.WORKOUT_EXERCISE,
    hover(item, monitor) {
      if (!isSelectionMode && !ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      const nextIndex = moveWorkoutExercise(item.id, workout_exercise._id);
      if (Number.isInteger(nextIndex)) item.index = nextIndex;
    }
  });

  const [{ isDragging }, drag] = useDrag({
    type: ItemType.WORKOUT_EXERCISE,
    item: { type: ItemType.WORKOUT_EXERCISE, id: workout_exercise._id, index },
    end: (_draggedItem, monitor) => {
      // Persist on drop only — see DraggableCompactExercise for rationale.
      if (onDragEnd) onDragEnd(monitor.didDrop());
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: () => !structureLocked && !readOnly && isLongPress && _isWorkoutEditable(workout)
  });

  useEffect(() => {
    if (!structureLocked && !readOnly && _isWorkoutEditable(workout)) {
      drag(drop(ref));
    } else {
      drag(null);
      drop(null);
    }
  }, [workout, drag, drop, readOnly, structureLocked]);

  const markUserInitiated = (e) => {
    if (e && isInteractiveElement(e.target)) return;
    userInitiatedScrollRef.current = true;
  };

  const handleTouchStart = (e) => {
    markUserInitiated(e);
    longPressTimeout.current = setTimeout(() => {
      setIsLongPress(true);
    }, 500);
  };

  const handleTouchEnd = () => {
    clearTimeout(longPressTimeout.current);
    setIsLongPress(false);
  };

  const handleFocus = () => {
    if (setOngoingExerciseId && !workout.is_template) {
      selectionLog('focus row', {
        workoutId: workout?._id,
        workoutStatus: workout?.status,
        workoutExerciseId: workout_exercise?._id,
      });
      setOngoingExerciseId(workout_exercise._id);

      // Only scroll when the user explicitly triggered selection (click/tap/keyboard).
      if (userInitiatedScrollRef.current && ref.current) {
        userInitiatedScrollRef.current = false;
        try {
          scrollPageIntoView(ref.current, { behavior: 'smooth', block: 'center', inline: 'nearest' });
        } catch (_) {
          scrollPageIntoView(ref.current);
        }

        // While the history chart mounts/animates, keep this row centered.
        stabilizeCenteredScroll(ref.current, 1200);
      }
    }
  };

  // Clicking anywhere in a collapsed exercise must select it. The row is
  // focusable, but a click on a plain (non-focusable) child does not reliably
  // move browser focus to the wrapper, so selection is driven explicitly here
  // instead of relying solely on onFocus.
  const selectFromPointer = (e) => {
    if (isInteractiveElement(e.target) && !e.target.closest('.exercise-title')) return;
    markUserInitiated(e);
    if (ref.current && typeof ref.current.focus === 'function') {
      ref.current.focus({ preventScroll: true });
    }
    handleFocus();
  };

  const handleSelectClick = (e) => {
    if (isInteractiveElement(e.target)) return;
    const start = selectionPointerStartRef.current;
    selectionPointerStartRef.current = null;
    if (start && !isSelectionTap({ start, end: { x: e.clientX, y: e.clientY } })) return;
    selectFromPointer(e);
  };

  const handleSelectPointerDown = (e) => {
    // Selection is intentionally deferred until click so a scroll/drag never
    // promotes the row into the selected state.
    if (!isOngoing && !isInteractiveElement(e.target)) {
      selectionPointerStartRef.current = { x: e.clientX, y: e.clientY };
    }
  };

  const handleBlur = (e) => {
    // Check if the new focused element is within another DraggableWorkoutExercise
    const relatedTarget = e.relatedTarget;

    selectionLog('blur row', {
      workoutId: workout?._id,
      workoutStatus: workout?.status,
      workoutExerciseId: workout_exercise?._id,
      hasRelatedTarget: Boolean(relatedTarget),
      relatedTag: relatedTarget?.tagName,
      relatedClass: relatedTarget?.className,
    });

    // If there's no related target, do NOT clear selection.
    // This happens frequently during rerenders while the activity stream is loading,
    // and clearing here causes the selected exercise to unexpectedly unselect.
    if (setOngoingExerciseId && !relatedTarget) {
      return;
    }

    // Determine the workout section associated with THIS draggable element
    const currentWorkoutSection = ref.current ? ref.current.closest('.workout-section') : null;
    const isWithinWorkout = currentWorkoutSection && currentWorkoutSection.contains(relatedTarget);

    selectionLog('blur evaluate', {
      workoutId: workout?._id,
      workoutExerciseId: workout_exercise?._id,
      isWithinWorkout,
      willClear: Boolean(setOngoingExerciseId && !isWithinWorkout)
    });

    // Do not clear selection on blur.
    // Selection should persist until the user explicitly selects another exercise.
    // (For example: toggling units moves focus outside the workout and would otherwise unselect.)
    if (setOngoingExerciseId && !isWithinWorkout) {
      selectionLog('blur left workout section (selection preserved)', {
        workoutId: workout?._id,
        fromWorkoutExerciseId: workout_exercise?._id,
      });
    }
  };

  return (
    <div
      ref={ref}
      tabIndex={0}
      onFocus={handleFocus}
      onPointerDown={handleSelectPointerDown}
      onClick={handleSelectClick}
      onBlur={handleBlur} // Add the blur handler
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchEnd}
      onMouseDown={(e) => {
        markUserInitiated(e);
        handleTouchStart(e);
      }}
      onMouseUp={handleTouchEnd}
      onMouseLeave={handleTouchEnd}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          markUserInitiated(e);
        }
      }}
      style={{ opacity: !structureLocked && !readOnly && _isWorkoutEditable(workout) && isDragging ? 0.5 : 1 }}
      data-workout-exercise-id={workout_exercise?._id}
      className="workout-draggable-wrapper"
      inert={isSelectionMode ? '' : undefined}
    >
        <WorkoutExercise
          key={workout_exercise._id}
          workout={workout}
          workout_exercise={workout_exercise}
          selectionOrder={selectionOrder}
          exercises={exercises}
          editable={
            (allowValueEditingWhenStructureLocked || !structureLocked) &&
            !readOnly &&
            !isSelectionMode &&
            _isWorkoutEditable(workout) &&
            (
              workout.is_template ||
              workout.status !== 'ongoing' ||
              isOngoing
            )
          }
          isTemplateMode={workout.is_template}
          backendService={backendService}
          modalService={modalService}
          refreshWorkout={refreshWorkout}
          onCoachSuggestionResolved={onCoachSuggestionResolved}
          isOngoing={isOngoing}
          allowCompletion={!readOnly && (workout.is_template || workout.status === 'ongoing')}
          structureLocked={structureLocked}
          isComplianceCritical={isComplianceCritical}
          recommendation={recommendation}
          readOnly={readOnly}
          isLoadingRecommendation={isLoadingRecommendation}
          isUpdatingRecommendation={isUpdatingRecommendation}
          onSubmitMessage={onSubmitMessage}
          onAnswerQuestion={onAnswerQuestion}
          onCompletedSetFieldChanged={onCompletedSetFieldChanged}
          setDateFilter={setDateFilter}
          coachImageUrl={coachImageUrl}
          coachEmoji={coachEmoji}
          showInlineCoach={showInlineCoach}
          proposalAnnotations={proposalAnnotations}
          onApproveProposal={onApproveProposal}
          busyStrategyProposalId={busyStrategyProposalId}
          coachAddedSetEffects={coachAddedSetEffects}
          onCoachAddedSetDisplayed={onCoachAddedSetDisplayed}
          coachFieldEffects={coachFieldEffects}
          onCoachFieldEffectDisplayed={onCoachFieldEffectDisplayed}
        >
        </WorkoutExercise>
    </div>
  );
};

/**
 * Custom Drag Layer - renders the dragged item following the cursor
 */
const CustomDragLayer = () => {
  const { isDragging, item, currentOffset } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    item: monitor.getItem(),
    currentOffset: monitor.getSourceClientOffset(),
  }));

  if (!isDragging || !currentOffset || !item) {
    return null;
  }

  const transform = `translate(${currentOffset.x}px, ${currentOffset.y}px)`;

  return (
    <div className="custom-drag-layer">
      <div className="drag-preview" style={{ transform }}>
        <div className="reorder-drag-handle">
          <Bars3Icon style={{ width: 20, height: 20 }} />
        </div>
        <div className="reorder-exercise-content">
          <span className="reorder-exercise-name">{item.exerciseName || 'Exercise'}</span>
          {item.setCount && <span className="reorder-exercise-sets">{item.setCount} sets</span>}
        </div>
      </div>
    </div>
  );
};

/**
 * Compact draggable exercise for reorder mode - simpler than full DraggableWorkoutExercise
 * Uses useLayoutEffect for smooth FLIP animations
 */
const DraggableCompactExercise = ({
  workout_exercise,
  index,
  moveWorkoutExercise,
  onDragEnd,
  exerciseName,
  setCount,
  isSuperset = false,
  supersetExercises = []
}) => {
  const ref = useRef(null);

  // Call this before the move happens
  const [{ isOver }, drop] = useDrop({
    accept: ItemType.WORKOUT_EXERCISE,
    hover(item, monitor) {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      
      // A taller superset can remain under the pointer after a single row
      // crosses it. Require its midpoint in the intended direction so the next
      // hover cannot immediately reverse the move in that overlap band.
      if (isSuperset) {
        const pointer = monitor.getClientOffset?.();
        if (pointer) {
          const rect = ref.current.getBoundingClientRect();
          const midpoint = (rect.top + rect.bottom) / 2;
          if (dragIndex < hoverIndex && pointer.y < midpoint) return;
          if (dragIndex > hoverIndex && pointer.y > midpoint) return;
        }
      }
      // Keep immediate entry for the compact single-row targets.
      
      // Capture all positions before move
      const allItems = document.querySelectorAll('.reorder-compact-item:not(.dragging)');
      allItems.forEach(item => {
        item._prevRect = item.getBoundingClientRect();
      });
      
      const nextIndex = moveWorkoutExercise(item.id, workout_exercise._id);
      if (Number.isInteger(nextIndex)) item.index = nextIndex;
      
      // Animate after React updates DOM
      requestAnimationFrame(() => {
        allItems.forEach(item => {
          if (!item._prevRect) return;
          const newRect = item.getBoundingClientRect();
          const deltaY = item._prevRect.top - newRect.top;
          
          if (Math.abs(deltaY) > 1) {
            item.style.transform = `translateY(${deltaY}px)`;
            item.style.transition = 'none';
            
            requestAnimationFrame(() => {
              item.style.transition = 'transform 0.2s ease-out';
              item.style.transform = 'translateY(0)';
            });
          }
          delete item._prevRect;
        });
      });
    },
    collect: (monitor) => ({
      isOver: monitor.isOver()
    })
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: ItemType.WORKOUT_EXERCISE,
    item: { 
      type: ItemType.WORKOUT_EXERCISE, 
      id: workout_exercise._id, 
      index,
      exerciseName: isSuperset ? supersetExercises.map(e => e.name).join(' + ') : exerciseName,
      setCount: isSuperset ? null : setCount,
      isSuperset
    },
    end: (_draggedItem, monitor) => {
      // Persist the new order on drop only. Persisting on every hover triggered
      // an `activity_updated` WebSocket round-trip mid-drag, which re-rendered
      // the parent and aborted the drag (visible as `end` firing before mouseup
      // on the second drag attempt).
      if (onDragEnd) onDragEnd(monitor.didDrop());
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Use empty image as default drag preview (we render custom one)
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={`reorder-compact-item ${isDragging ? 'dragging' : ''} ${isOver && !isDragging ? 'drop-target' : ''} ${isSuperset ? 'is-superset' : ''}`}
    >
      <div className="reorder-drag-handle">
        <Bars3Icon style={{ width: 20, height: 20 }} />
      </div>
      {isSuperset ? (
        <div className="reorder-superset-content">
          <div className="reorder-superset-label">
            <LinkIcon style={{ width: 12, height: 12 }} />
            SUPERSET
          </div>
          <div className="reorder-superset-exercises">
            {supersetExercises.map((ex, idx) => (
              <span key={idx} className="reorder-superset-exercise-name">
                {ex.name}{idx < supersetExercises.length - 1 ? ' + ' : ''}
              </span>
            ))}
          </div>
        </div>
      ) : (
        <div className="reorder-exercise-content">
          <span className="reorder-exercise-name">{exerciseName}</span>
          <span className="reorder-exercise-sets">{setCount} sets</span>
        </div>
      )}
    </div>
  );
};

function Workout({ workout, navigationRequest = null, finishActivity, completion = IDLE_COMPLETION, onWorkoutNameChange, onActivityRefresh, onReorderModeChange, exercises, modalService, backendService, onReady, readOnly = false, recommendationVersion = 0, refreshKey = 0, coachImageUrl = null, coachEmoji = '🥋', coachIdentitySettled = false, travelReplacementEnabled = false, onViewReplacedWorkout = null }) {
  const hasTravelReplacement = Boolean(workout.travel_replacement_id || workout.data?.travel_replacement_id || workout.data?.travel_replacement_activity_id);
  const isTravelReplacement = workout.source?.compliance_role === 'travel_replacement';
  const isTrackedActivityReplacement = isTravelReplacement
    && workout.data?.travel_replacement?.mode === 'tracked_activity';
  const isExistingActivityReplacementEvidence = Boolean(
    workout.travel_replacement_for_activity_id
      && workout.travel_replacement_assignment?.kind === 'existing_activity'
  );
  const replacedWorkoutName = workout.source?.replaces_activity_name
    || workout.data?.travel_replacement?.original_workout_name
    || 'Original workout';
  const isDayActionEligible = useDaySwapEligibility(workout);
  // A linked original is immutable while its replacement owns the obligation.
  const isWorkoutEditable = readOnly || hasTravelReplacement ? () => false : _isWorkoutEditable;
  const { position: geoPosition } = useGeolocationService() || {};
  const [workout_exercises, setWorkoutExerciseState] = useState(() =>
    getInitialWorkoutExercises({ workout, readOnly })
  );
  const workoutExercisesRef = useRef(workout_exercises);
  const draggingExerciseOrderRef = useRef(false);
  const exerciseOrderSaveRef = useRef(null);
  const [exerciseOrderFailure, setExerciseOrderFailure] = useState(null);
  const exerciseOrderOwner = backendService.user?._id;
  const exerciseOrderGeneration = backendService.backgroundIdentityGeneration;
  const exerciseOrderScope = useMemo(() => ({
    backendService, workoutId: String(workout._id), readOnly, hasTravelReplacement,
    owner: exerciseOrderOwner, generation: exerciseOrderGeneration,
  }), [backendService, workout._id, readOnly, hasTravelReplacement, exerciseOrderOwner, exerciseOrderGeneration]);
  const currentExerciseOrderScopeRef = useRef(exerciseOrderScope);
  if (currentExerciseOrderScopeRef.current !== exerciseOrderScope) {
    currentExerciseOrderScopeRef.current = exerciseOrderScope;
    draggingExerciseOrderRef.current = false;
    workoutExercisesRef.current = getInitialWorkoutExercises({ workout, readOnly });
  }
  const isExerciseOrderScopeCurrent = () => mountedRef.current
    && currentExerciseOrderScopeRef.current === exerciseOrderScope
    && backendService.user?._id === exerciseOrderScope.owner
    && backendService.backgroundIdentityGeneration === exerciseOrderScope.generation;
  const setWorkoutExercises = rows => {
    if (!isExerciseOrderScopeCurrent()) return;
    if (draggingExerciseOrderRef.current) {
      const byId = new Map(rows.map(row => [String(row._id), row]));
      const ids = workoutExercisesRef.current.map(row => String(row._id));
      const known = new Set(ids);
      const ordered = ids.filter(id => byId.has(id)).map(id => byId.get(id));
      let index = 0;
      rows = rows.map(row => known.has(String(row._id)) ? ordered[index++] : row);
    }
    workoutExercisesRef.current = rows;
    setWorkoutExerciseState(rows);
  };
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [ongoingExerciseId, setOngoingExerciseId] = useState(null);
  const [hydratedWorkoutId, setHydratedWorkoutId] = useState(null);
  const [navigationSelection, setNavigationSelection] = useState(null);
  const appliedNavigationRef = useRef(null);
  const navigationTargetsRef = useRef(new Map());
  const [workoutStatus, setWorkoutStatus] = useState(workout.status);
  const [exerciseRecommendations, setExerciseRecommendations] = useState({});
  const [updatingExerciseIds, setUpdatingExerciseIds] = useState(new Set()); // Track which exercises are being updated
  const [updatedExerciseIds, setUpdatedExerciseIds] = useState(new Set()); // Track recently updated exercises for animation
  const [workoutCoachThreadRefreshNonce, setWorkoutCoachThreadRefreshNonce] = useState(0);
  const [strategyProposals, setStrategyProposals] = useState([]);
  const [busyStrategyProposalId, setBusyStrategyProposalId] = useState(null);
  const busyStrategyProposalRef = useRef(null);
  const [strategyProposalError, setStrategyProposalError] = useState('');
  const [strategyProposalReadError, setStrategyProposalReadError] = useState('');
  const [strategyProposalsLoading, setStrategyProposalsLoading] = useState(false);
  const strategyProposalReadRef = useRef(null);
  const {
    effects: coachFieldEffects,
    publish: publishCoachFieldEffects,
    markDisplayed: markCoachFieldEffectDisplayed,
  } = useCoachAppliedFieldEffects(workout._id);
  
  const {
    effects: coachAddedSetEffects,
    publish: publishCoachAddedSets,
    markDisplayed: onCoachAddedSetDisplayed,
  } = useCoachAddedSetEffects(workout._id);

  // Superset selection state
  const [supersetSelectionMode, setSupersetSelectionMode] = useState(false);
  const [selectedForSuperset, setSelectedForSuperset] = useState(new Set());
  const [supersetSaving, setSupersetSaving] = useState(false);
  const supersetSelectionOrder = useMemo(() => new Map(
    [...selectedForSuperset].map((id, index) => [id, index + 1])
  ), [selectedForSuperset]);
  const supersetScope = useMemo(() => ({ active: true, backendService, workoutId: workout._id, readOnly }), [backendService, workout._id, readOnly]);
  useEffect(() => {
    supersetScope.active = true;
    setSelectedForSuperset(new Set());
    setSupersetSelectionMode(false);
    setSupersetSaving(false);
    return () => { supersetScope.active = false; };
  }, [supersetScope]);
  useEffect(() => {
    const eligible = new Set(workout_exercises.filter(exercise => exercise.status !== 'removed').map(exercise => exercise._id));
    setSelectedForSuperset(previous => {
      const retained = [...previous].filter(id => eligible.has(id));
      return retained.length === previous.size ? previous : new Set(retained);
    });
  }, [workout_exercises]);

  const strategyProposalScope = useMemo(() => ({
    active: false, backendService, readOnly, workoutId: workout._id,
    isTemplate: workout.is_template, planEntryId: workout?.source?.activity_plan_entry_id,
  }), [backendService, readOnly, workout._id, workout.is_template, workout?.source?.activity_plan_entry_id]);
  useEffect(() => {
    strategyProposalScope.active = true;
    strategyProposalReadRef.current = null;
    setStrategyProposals([]);
    setStrategyProposalReadError('');
    setStrategyProposalError('');
    setStrategyProposalsLoading(false);
    return () => { strategyProposalScope.active = false; };
  }, [strategyProposalScope]);

  const loadStrategyProposals = useCallback(async ({ fresh = false } = {}) => {
    if (readOnly || workout.is_template || !workout?.source?.activity_plan_entry_id || !backendService?.fetchStrategyProposals) {
      setStrategyProposals([]);
      return [];
    }
    if (!strategyProposalScope.active) return [];
    if (!fresh && strategyProposalReadRef.current) return strategyProposalReadRef.current.promise;
    const request = {};
    strategyProposalReadRef.current = request;
    const isCurrent = () => strategyProposalScope.active && strategyProposalReadRef.current === request;
    setStrategyProposalsLoading(true);
    request.promise = (async () => {
      try {
        const proposals = await backendService.fetchStrategyProposals();
        if (!isCurrent()) return [];
        setStrategyProposals(Array.isArray(proposals) ? proposals : []);
        setStrategyProposalReadError('');
        return proposals;
      } catch (proposalError) {
        if (isCurrent()) setStrategyProposalReadError(proposalError?.message || 'Could not refresh Coach suggestions.');
        return [];
      } finally {
        if (isCurrent()) {
          strategyProposalReadRef.current = null;
          setStrategyProposalsLoading(false);
        }
      }
    })();
    return request.promise;
  }, [backendService, readOnly, workout.is_template, workout?.source?.activity_plan_entry_id, strategyProposalScope]);

  useEffect(() => {
    loadStrategyProposals();
    const handleProposalUpdate = () => loadStrategyProposals({ fresh: true });
    window.addEventListener('regimen:strategy-proposals-updated', handleProposalUpdate);
    return () => window.removeEventListener('regimen:strategy-proposals-updated', handleProposalUpdate);
  }, [loadStrategyProposals]);
  
  // Reorder mode state
  const [reorderMode, setReorderMode] = useState(false);
  useEffect(() => {
    onReorderModeChange?.(reorderMode);
    return () => onReorderModeChange?.(false);
  }, [onReorderModeChange, reorderMode]);
  
  // Rest day and travel-replacement state
  const [showRestPrompt, setShowRestPrompt] = useState(false);
  const [restSubmitting, setRestSubmitting] = useState(false);
  const [restError, setRestError] = useState(null);
  const [travelPreviewLoading, setTravelPreviewLoading] = useState(false);
  const [travelError, setTravelError] = useState(null);
  const travelPreviewRequestRef = useRef(0);

  useEffect(() => {
    travelPreviewRequestRef.current += 1;
    setTravelPreviewLoading(false);
    setTravelError(null);
  }, [travelReplacementEnabled, backendService, workout._id]);

  useEffect(() => () => {
    travelPreviewRequestRef.current += 1;
  }, []);

  // Gym state
  const [gymDetails, setGymDetails] = useState(null);
  const [gymConfirmed, setGymConfirmed] = useState(false);
  const [userGyms, setUserGyms] = useState([]);
  const [showGymSelector, setShowGymSelector] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  
  const hasSignaledReadyRef = useRef(false);
  const ongoingExerciseIdRef = useRef(null);
  const refreshSeqRef = useRef(0);
  const mountedRef = useRef(false);
  const workoutScopeRef = useRef(String(workout?._id || ''));
  workoutScopeRef.current = String(workout?._id || '');
  const exerciseListRef = useRef(null);
  const exerciseStateKeysRef = useRef({}); // Track individual exercise state keys
  const coachRefreshSchedulerRef = useRef(null);
  if (!coachRefreshSchedulerRef.current) coachRefreshSchedulerRef.current = createWorkoutCoachRefreshScheduler();
  const recommendationControllersRef = useRef({});
  // One waiter per pending member review, removed when its fetch settles.
  const reviewPublicationWaitersRef = useRef({});
  const abortControllersRef = useRef({}); // Conversation requests remain independent from automatic reviews
  const resolvedCoachActionIdsRef = useRef(new Set());
  const quarantinedCoachActionIdsRef = useRef(new Set());
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      refreshSeqRef.current += 1;
    };
  }, []);
  useEffect(() => {
    refreshSeqRef.current += 1;
    setExerciseRecommendations({});
    resolvedCoachActionIdsRef.current.clear();
    quarantinedCoachActionIdsRef.current.clear();
    exerciseStateKeysRef.current = {};
  }, [workout._id]);

  // When displaying a multipart workout (sets spanning multiple days), filter exercises
  // to only include those with sets completed on the target date.
  const partDateFilter = workout._partDateFilter || null;
  const partNumber = workout._partNumber || null;
  const totalParts = workout._totalParts || null;

  // Guess user timezone from Intl API (Activities.js uses profile timezone but that is not
  // available here; the local timezone is an acceptable fallback for date-key comparison).
  const userTimezone = useMemo(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'UTC'; }
  }, []);

  const displayExercises = useMemo(() => {
    if (!partDateFilter) return workout_exercises;
    // Filter to exercises that have at least one completed set on the target date
    return workout_exercises.filter(ex => {
      if (ex.status === 'removed') return false;
      return (ex.sets || []).some(s =>
        s.complete && s.completed_at && getDateKey(s.completed_at, userTimezone) === partDateFilter
      );
    });
  }, [workout_exercises, partDateFilter, userTimezone]);

  // Group exercises by superset for rendering
  const groupedExercises = useMemo(() => {
    const groups = [];
    let currentGroup = null;
    
    displayExercises.forEach((exercise, index) => {
      const supersetGroup = exercise.superset_group;
      
      if (supersetGroup !== null && supersetGroup !== undefined) {
        // Part of a superset
        if (currentGroup && currentGroup.supersetGroup === supersetGroup) {
          // Continue the current superset group
          currentGroup.exercises.push({ exercise, index });
        } else {
          // Start a new superset group
          if (currentGroup) {
            groups.push(currentGroup);
          }
          currentGroup = {
            type: 'superset',
            supersetGroup,
            exercises: [{ exercise, index }]
          };
        }
      } else {
        // Not part of a superset
        if (currentGroup) {
          groups.push(currentGroup);
          currentGroup = null;
        }
        groups.push({
          type: 'single',
          exercises: [{ exercise, index }]
        });
      }
    });
    
    // Don't forget the last group
    if (currentGroup) {
      groups.push(currentGroup);
    }
    
    return groups;
  }, [displayExercises]);

  // Explicit shortcut navigation is different from automatic resume selection:
  // use displayed order, not the current selection/last-completed heuristic.
  useEffect(() => {
    if (!navigationRequest?.firstIncomplete || navigationRequest.id !== String(workout._id)
      || readOnly || hydratedWorkoutId !== String(workout._id)
      || appliedNavigationRef.current === navigationRequest) return undefined;
    const target = workoutStatus === 'ongoing' && !isWorkoutExpired(workout)
      ? displayExercises.find(ex => ex.status !== 'removed' && (!ex.sets?.length || ex.sets.some(set => !set.complete)))
      : null;
    const selectedId = target?._id || null;
    const setIndex = target?.sets?.findIndex(set => !set.complete) ?? -1;
    ongoingExerciseIdRef.current = selectedId;
    setOngoingExerciseId(selectedId);
    setNavigationSelection({ request: navigationRequest, exerciseId: selectedId });
    const frame = requestAnimationFrame(() => {
      const card = navigationTargetsRef.current.get(selectedId)
        || document.getElementById(`activity-${navigationRequest.id}`);
      if (!card) return;
      const row = setIndex >= 0 ? Array.from(card.querySelectorAll('[data-shortcut-set-index]')).find(candidate =>
        Number(candidate.dataset.shortcutSetIndex) === setIndex
        && (!candidate.dataset.shortcutExerciseId || candidate.dataset.shortcutExerciseId === String(selectedId))) : null;
      const element = row || card;
      appliedNavigationRef.current = navigationRequest;
      card.focus({ preventScroll: true });
      // Center an actual set row clear of top-bar and toast overlays. Keep the
      // original top clearance only for the overview/empty-set card fallback.
      if (!row) element.style.scrollMarginTop = '140px';
      scrollPageIntoView(element, {
        behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: row ? 'center' : 'start', inline: 'nearest',
      });
      navigationRequest.onHandled?.();
    });
    return () => cancelAnimationFrame(frame);
  }, [navigationRequest, workout, workoutStatus, readOnly, hydratedWorkoutId, displayExercises]);

  useEffect(() => {
    setWorkoutStatus(workout.status);
  }, [workout.status]);

  useEffect(() => {
    if (selectionDebugEnabled()) {
      // eslint-disable-next-line no-console
      console.log('[SelectionDebug] enabled (set localStorage.debugSelection = 1/true/on)');
    }
  }, []);

  // Keep a synchronous reference to the latest selection so async refreshes
  // don't accidentally use a stale captured value.
  useEffect(() => {
    ongoingExerciseIdRef.current = ongoingExerciseId;
    selectionLog('selection state changed', {
      workoutId: workout?._id,
      ongoingExerciseId
    });
  }, [ongoingExerciseId, workout?._id]);

  // Sync geolocation context position into local userLocation state
  useEffect(() => {
    if (geoPosition && geoPosition.latitude != null && geoPosition.longitude != null) {
      setUserLocation({ latitude: geoPosition.latitude, longitude: geoPosition.longitude });
    }
  }, [geoPosition]);

  // Fetch user's saved gyms and initialize gym state from workout
  useEffect(() => {
    // Initialize gym state from workout data
    if (workout.gym_details) {
      setGymDetails(workout.gym_details);
      setGymConfirmed(workout.data?.gym_confirmed || false);
    }
    
    if (readOnly) return;
    let cancelled = false;
    // Fetch user's gyms for the selector
    const fetchGyms = async () => {
      try {
        const gyms = await backendService.getGyms();
        if (!cancelled) setUserGyms(gyms);
      } catch (error) {
        console.error('Error fetching gyms:', error);
      }
    };
    fetchGyms();
    return () => { cancelled = true; };
  }, [readOnly, workout._id, workout.gym_details, workout.data?.gym_confirmed, backendService]);


  // Function to choose the active exercise based on the workout status and exercises
  const chooseActiveExercise = (exercises, currentId) => {
    // No exercises means no ongoing exercise
    if (!exercises || exercises.length === 0) return null;

    // Helper to check if an exercise is complete
    const isExerciseComplete = (ex) => {
      if (ex.status === 'removed') return true; // Treat removed as complete/skippable
      if (!ex.sets || ex.sets.length === 0) return false; // No sets = incomplete
      return ex.sets.every(set => set.complete);
    };

    // Helper to check if an exercise is partially complete (started but not finished)
    const isExercisePartial = (ex) => {
      if (ex.status === 'removed') return false;
      if (!ex.sets || ex.sets.length === 0) return false;
      const hasComplete = ex.sets.some(set => set.complete);
      const hasIncomplete = ex.sets.some(set => !set.complete);
      return hasComplete && hasIncomplete;
    };

    // 1. If we have a current exercise, stay on it unless it is removed.
    if (currentId) {
      const currentEx = exercises.find(e => e._id === currentId);
      if (currentEx && currentEx.status !== 'removed') {
        return currentId;
      }
    }

    // 2. Look for the first "Partially Complete" exercise.
    // This handles the case where we went back to a previous exercise or are in the middle of one.
    const partialEx = exercises.find(ex => isExercisePartial(ex));
    if (partialEx) {
      return partialEx._id;
    }

    // 3. If no partials, find the first incomplete exercise that comes AFTER the last fully completed exercise.
    // This handles the "Skipping" case (A skipped, B done -> go to C).
    
    // Find the index of the last completed exercise
    let lastCompletedIndex = -1;
    for (let i = 0; i < exercises.length; i++) {
      if (isExerciseComplete(exercises[i])) {
        lastCompletedIndex = i;
      }
    }

    // Find the next incomplete exercise after the last completed one
    for (let i = lastCompletedIndex + 1; i < exercises.length; i++) {
      if (!isExerciseComplete(exercises[i])) {
        return exercises[i]._id;
      }
    }

    // 4. Fallback: If we couldn't find one after the last completed (e.g. maybe none are completed),
    // just return the first incomplete one.
    const firstIncomplete = exercises.find(ex => !isExerciseComplete(ex));
    if (firstIncomplete) {
      return firstIncomplete._id;
    }

    return null;
  };

  const refreshWorkout = async () => {
    if (readOnly) return false;
    const seq = ++refreshSeqRef.current;
    const requestedWorkoutId = String(workout?._id || '');
    const startedAt = Date.now();

    selectionLog('refreshWorkout start', {
      seq,
      workoutId: workout?._id,
      capturedWorkoutStatusProp: workout?.status,
      stateWorkoutStatus: workoutStatus,
      selectedIdRef: ongoingExerciseIdRef.current,
    });

    try {
      const activity = await backendService.fetchActivity(workout._id);
      if (!mountedRef.current || refreshSeqRef.current !== seq || workoutScopeRef.current !== requestedWorkoutId) {
        return false;
      }
      const exercises = activity.data.exercises;
      setWorkoutExercises(exercises);
      setHydratedWorkoutId(requestedWorkoutId);
      onWorkoutNameChange(activity.name);

      const nextStatus = activity.status;
      setWorkoutStatus(nextStatus);
      
      // Update gym state from refreshed activity
      if (activity.gym_details) {
        setGymDetails(activity.gym_details);
        setGymConfirmed(activity.data?.gym_confirmed || false);
      } else {
        setGymDetails(null);
        setGymConfirmed(false);
      }

      // IMPORTANT: derive selection from the freshly-fetched activity status.
      // During activity stream stabilization we can rerender frequently; if we rely
      // on the (stale) workoutStatus state here, it can briefly evaluate as not-ongoing
      // and clear the selected exercise.
      const isExpired = isWorkoutExpired(activity);
      const nextOngoingId = (isExpired || nextStatus !== 'ongoing')
        ? null
        : chooseActiveExercise(exercises, ongoingExerciseIdRef.current);

      selectionLog('refreshWorkout choose', {
        seq,
        workoutId: workout?._id,
        elapsedMs: Date.now() - startedAt,
        nextStatus,
        isExpired,
        prevSelectedId: ongoingExerciseIdRef.current,
        nextSelectedId: nextOngoingId,
        exerciseCount: Array.isArray(exercises) ? exercises.length : null,
      });

      setOngoingExerciseId(nextOngoingId);
      
      // Propagate updated activity to parent so consistency points update
      if (onActivityRefresh) {
        onActivityRefresh(activity);
      }
      
      if (onReady && !hasSignaledReadyRef.current) {
        hasSignaledReadyRef.current = true;
        onReady();
      }

      return activity;

    } catch (error) {
      if (!mountedRef.current || refreshSeqRef.current !== seq || workoutScopeRef.current !== requestedWorkoutId) {
        return false;
      }
      selectionLog('refreshWorkout error', {
        seq,
        workoutId: workout?._id,
        elapsedMs: Date.now() - startedAt,
        message: error?.message,
        status: error?.response?.status,
      });
      console.error('Error fetching workout exercises:', error);
      if (error.response && error.response.status === 404) {
        // Activity not found, notify parent to refresh
        if (onActivityRefresh) {
          onActivityRefresh(null);
        }
      }
      if (onReady && !hasSignaledReadyRef.current) {
        hasSignaledReadyRef.current = true;
        onReady();
      }
      return false;
    }
  }

  useEffect(() => {
    if (readOnly) return;
    const handleWorkoutUpdated = event => {
      if (String(event?.detail?.workoutId || '') !== String(workout?._id || '')) return;
      refreshWorkout();
    };
    window.addEventListener('regimen:workout-updated', handleWorkoutUpdated);
    return () => window.removeEventListener('regimen:workout-updated', handleWorkoutUpdated);
    // refreshWorkout intentionally follows the mounted workout id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly, workout?._id]);

  useEffect(() => {
    // Social activity details are already hydrated by the read-only social
    // endpoint. Do not re-fetch through the owner-only activity endpoint:
    // that request cannot return another user's workout and would replace the
    // visible sets with incomplete summary data.
    if (readOnly) return;

    selectionLog('Workout useEffect refresh', {
      workoutId: workout?._id,
      statusProp: workout?.status
    });
    refreshWorkout();
    // refreshWorkout depends on captured props/state; lint complains about re-definition per render.
    // In this case we intentionally re-fetch when workout changes or after an upgrade (refreshKey).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly, workout?._id, refreshKey]);

  const reconcileResolvedCoachSuggestion = async (resolution = {}) => {
    const beforeExercises = workout_exercises;
    const reconciliationScope = String(workout?._id || '');
    const hasPerResultResolution = resolution.status === 'resolved'
      && Array.isArray(resolution.response?.results);
    const proposedTargets = (resolution.status === 'approved' || hasPerResultResolution)
      ? buildCoachAppliedTargets(resolution.actions, resolution.response)
      : [];
    const proposedRows = (resolution.status === 'approved' || hasPerResultResolution)
      ? buildCoachAddedSetTargets(resolution.actions, resolution.response) : [];
    // Fresh atomic IDs are already authoritative. Publish intent before refreshes
    // race each other; the row hook waits for this exact canonical displayed row.
    // Numeric field confirmations still require the strict refreshed snapshot below.
    if (!readOnly && mountedRef.current && workoutScopeRef.current === reconciliationScope) {
      publishCoachAddedSets(proposedRows);
    }
    const actionIds = Array.isArray(resolution.actionIds) ? resolution.actionIds.filter(Boolean) : [];
    const missingActionIds = Array.isArray(resolution.missingActionIds) ? resolution.missingActionIds.filter(Boolean) : [];
    // Keep successful resolutions monotonic for this mounted workout. A slower
    // recommendation request that began before Apply/Dismiss must not restore a
    // pending card while the canonical lifecycle read catches up.
    if (['approved', 'rejected', 'resolved', 'superseded'].includes(resolution.status)) {
      actionIds.forEach(actionId => resolvedCoachActionIdsRef.current.add(actionId));
      setExerciseRecommendations(current => hideResolvedCoachActions(
        current,
        resolvedCoachActionIdsRef.current,
      ));
    }
    if (missingActionIds.length > 0) {
      missingActionIds.forEach(actionId => quarantinedCoachActionIdsRef.current.add(actionId));
      setExerciseRecommendations(current => replaceExerciseRecommendations(
        current,
        current,
        resolvedCoachActionIdsRef.current,
        quarantinedCoachActionIdsRef.current,
      ));
    }

    const workoutPromise = resolution.refreshOnly ? Promise.resolve(true) : refreshWorkout();
    const reconciliationRefreshSeq = refreshSeqRef.current;
    const recommendationsPromise = Promise.allSettled([
      backendService.getStoredWorkoutRecommendations(workout._id),
    ]);
    const [workoutResult] = await Promise.allSettled([workoutPromise]);
    const reconciliationIsCurrent = mountedRef.current
      && workoutScopeRef.current === reconciliationScope
      && (resolution.refreshOnly || refreshSeqRef.current === reconciliationRefreshSeq);
    if (reconciliationIsCurrent && workoutResult.status === 'fulfilled' && workoutResult.value && proposedTargets.length > 0) {
      const targets = reconcileCoachAppliedTargets(proposedTargets, beforeExercises, workoutResult.value?.data?.exercises);
      publishCoachFieldEffects(targets);
    }

    // Canonical value feedback is independent of the Coach read. A slow or
    // failed recommendations request must not delay confirmed changed fields.
    const [recommendationsResult] = await recommendationsPromise;
    const recommendationsAreCurrent = mountedRef.current
      && workoutScopeRef.current === reconciliationScope
      && (resolution.refreshOnly || refreshSeqRef.current === reconciliationRefreshSeq);
    if (recommendationsAreCurrent && recommendationsResult.status === 'fulfilled') {
      setExerciseRecommendations(current => replaceExerciseRecommendations(
        current,
        recommendationsResult.value?.recommendations || {},
        resolvedCoachActionIdsRef.current,
        quarantinedCoachActionIdsRef.current,
      ));
      recommendationJob.reconcile?.(recommendationsResult.value);
    }
    return recommendationsAreCurrent
      && workoutResult.status === 'fulfilled'
      && workoutResult.value !== false
      && recommendationsResult.status === 'fulfilled';
  };

  const { hasAccess: hasRecommendationsFeature, loading: recommendationsFeatureLoading } = useFeature('ai_recommendations');
  const recommendationsEnabled = !readOnly && !workout.is_template;
  const canGenerateRecommendations = recommendationsEnabled && hasRecommendationsFeature && !recommendationsFeatureLoading;
  const recommendationJob = useWorkoutRecommendationJob({
    activityId: workout._id,
    backendService,
    enabled: recommendationsEnabled,
    autoGenerate: canGenerateRecommendations && !hasTravelReplacement && !isTravelReplacement && !isExistingActivityReplacementEvidence
      && ((workoutStatus === 'ongoing' && (workout_exercises.length > 0 || workout.data?.exercises?.length > 0))
        || (recommendationVersion > 0 && workoutStatus === 'pending')),
    version: recommendationVersion,
    onRecommendations: recommendations => setExerciseRecommendations(current => replaceExerciseRecommendations(
      current, recommendations, resolvedCoachActionIdsRef.current, quarantinedCoachActionIdsRef.current,
    )),
  });
  const isLoadingRecommendations = recommendationJob.isLoading;

  // Prescription refreshes belong to the edited exercise. The conversation
  // remains shared across the Superset independently of this cache key.
  const fetchSingleExerciseRecommendation = async (workoutExerciseId) => {
    if (readOnly) return;
    const coachExerciseId = workoutExerciseId;
    if (recommendationControllersRef.current[coachExerciseId]) {
      recommendationControllersRef.current[coachExerciseId].abort();
    }
    const abortController = new AbortController();
    recommendationControllersRef.current[coachExerciseId] = abortController;
    // A publication signal only wakes this member's next canonical read. It
    // is retained across the POST and an in-flight read, never rendered.
    const waiter = { taskId: null, early: [], signalled: false, wake: null };
    reviewPublicationWaitersRef.current[coachExerciseId] = waiter;

    try {
      setUpdatingExerciseIds(prev => new Set([...prev, coachExerciseId]));
      if (backendService.waitForWorkoutSetDrain) {
        await backendService.waitForWorkoutSetDrain(abortController.signal);
      } else {
        await backendService.waitForWorkoutSetUpdates?.();
      }
      if (abortController.signal.aborted || !mountedRef.current || workoutScopeRef.current !== String(workout._id)) return;
      let result = await backendService.getSingleExerciseRecommendation(workout._id, coachExerciseId, abortController.signal);
      if (result?.generation?.status === 'pending' && backendService.getStoredSingleExerciseRecommendation) {
        // The shared thread reports only one generation at a time. Poll this
        // member's durable task so another member cannot hide its completion.
        // External tasks may remain valid for fifteen minutes. Poll quickly
        // initially, then back off; all timers and reads share the member abort.
        // A matching publication signal ends the sleep early; the timer stays
        // the fallback when no signal arrives.
        waiter.taskId = String(result.generation.task_id);
        waiter.signalled = waiter.early.includes(waiter.taskId);
        waiter.early = [];
        for (let attempt = 0; attempt < 92 && !abortController.signal.aborted; attempt += 1) {
          const delay = attempt < 2 ? 2000 : 10000;
          await new Promise(resolve => {
            if (waiter.signalled) {
              waiter.signalled = false;
              resolve();
              return;
            }
            const finish = () => {
              clearTimeout(timer);
              abortController.signal.removeEventListener('abort', finish);
              waiter.wake = null;
              resolve();
            };
            const timer = setTimeout(finish, delay);
            waiter.wake = finish;
            abortController.signal.addEventListener('abort', finish, { once: true });
          });
          if (abortController.signal.aborted || !mountedRef.current || workoutScopeRef.current !== String(workout._id)) return;
          let polled;
          const readStartedAt = coachSequenceNow();
          try {
            polled = await backendService.getStoredSingleExerciseRecommendation(workout._id, coachExerciseId, abortController.signal);
            noteCoachCanonicalRead('set_review', String(coachExerciseId), readStartedAt);
          } catch (pollError) {
            if (abortController.signal.aborted) return;
            // A transient status-read outage does not cancel the durable task.
            // Keep checking within the task's bounded observation window.
            continue;
          }
          if (polled?.generation?.task_id !== result.generation.task_id) return;
          result = polled;
          if (result?.generation?.status !== 'pending') break;
        }
      }
      if (abortController.signal.aborted || !mountedRef.current || workoutScopeRef.current !== String(workout._id)) return;
      if (result?.generation) setWorkoutCoachThreadRefreshNonce(value => value + 1);
      if (result && result.recommendation) {
        // Exact task-fenced canonical body for this member: context matched.
        if (result.generation?.status && result.generation.status !== 'pending') noteCoachContextMatch('set_review', String(coachExerciseId));
        setExerciseRecommendations(current => mergeExerciseRecommendation(
          current,
          coachExerciseId,
          result.recommendation,
          resolvedCoachActionIdsRef.current,
          quarantinedCoachActionIdsRef.current,
        ));
        setWorkoutCoachThreadRefreshNonce(value => value + 1);
        await recommendationJob.reconcile();
        setUpdatedExerciseIds(prev => new Set([...prev, coachExerciseId]));
        setTimeout(() => {
          setUpdatedExerciseIds(prev => {
            const next = new Set(prev);
            next.delete(coachExerciseId);
            return next;
          });
        }, 1500);
      }
    } catch (error) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        console.debug(`Coach recommendation for ${coachExerciseId} was aborted`);
        return;
      }
      console.error('Error fetching Coach recommendation:', error);
    } finally {
      if (reviewPublicationWaitersRef.current[coachExerciseId] === waiter) delete reviewPublicationWaitersRef.current[coachExerciseId];
      if (recommendationControllersRef.current[coachExerciseId] === abortController) {
        setUpdatingExerciseIds(prev => {
          const next = new Set(prev);
          next.delete(coachExerciseId);
          return next;
        });
      }
    }
  };

  // Handle user submitting a message to the AI coach (not a response to a question)
  const handleSubmitMessage = async (workoutExerciseId, message, attachments = []) => {
    // Abort any previous request for this exercise
    if (abortControllersRef.current[workoutExerciseId]) {
      abortControllersRef.current[workoutExerciseId].abort();
    }
    const abortController = new AbortController();
    abortControllersRef.current[workoutExerciseId] = abortController;

    try {
      setUpdatingExerciseIds(prev => new Set([...prev, workoutExerciseId]));
      const uploaded = attachments.length > 0
        ? await backendService.uploadCoachAttachments(attachments)
        : [];
      const result = await backendService.submitExerciseMessage(
        workout._id,
        workoutExerciseId,
        message,
        false,
        abortController.signal,
        uploaded.map(attachment => attachment.id)
      );
      if (result?.generation) setWorkoutCoachThreadRefreshNonce(value => value + 1);
      if (result && result.recommendation) {
        setExerciseRecommendations(current => mergeExerciseRecommendation(
          current,
          workoutExerciseId,
          result.recommendation,
          resolvedCoachActionIdsRef.current,
          quarantinedCoachActionIdsRef.current,
        ));
        await recommendationJob.reconcile();
      }
    } catch (error) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        console.debug(`Message submission for ${workoutExerciseId} was aborted`);
        return;
      }
      console.error('Error submitting message:', error);
      throw error;
    } finally {
      if (abortControllersRef.current[workoutExerciseId] === abortController) {
        setUpdatingExerciseIds(prev => {
          const next = new Set(prev);
          next.delete(workoutExerciseId);
          return next;
        });
      }
    }
  };

  // Handle user answering a question from the AI coach
  const handleAnswerQuestion = async (workoutExerciseId, answer) => {
    // Abort any previous request for this exercise
    if (abortControllersRef.current[workoutExerciseId]) {
      abortControllersRef.current[workoutExerciseId].abort();
    }
    const abortController = new AbortController();
    abortControllersRef.current[workoutExerciseId] = abortController;

    try {
      setUpdatingExerciseIds(prev => new Set([...prev, workoutExerciseId]));
      const result = await backendService.submitExerciseMessage(workout._id, workoutExerciseId, answer, true, abortController.signal);
      if (result?.generation) setWorkoutCoachThreadRefreshNonce(value => value + 1);
      if (result && result.recommendation) {
        setExerciseRecommendations(current => mergeExerciseRecommendation(
          current,
          workoutExerciseId,
          result.recommendation,
          resolvedCoachActionIdsRef.current,
          quarantinedCoachActionIdsRef.current,
        ));
        await recommendationJob.reconcile();
      }
    } catch (error) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        console.debug(`Answer submission for ${workoutExerciseId} was aborted`);
        return;
      }
      console.error('Error answering question:', error);
    } finally {
      if (abortControllersRef.current[workoutExerciseId] === abortController) {
        setUpdatingExerciseIds(prev => {
          const next = new Set(prev);
          next.delete(workoutExerciseId);
          return next;
        });
      }
    }
  };

  // Handle field changes on already-completed sets — debounce and refresh AI recommendation
  const handleCompletedSetFieldChanged = useCallback((workoutExerciseId) => {
    if (readOnly || workoutStatus !== 'ongoing') return;

    const timerKey = workoutExerciseId;

    // An edit supersedes only the same member's in-flight recommendation.
    if (recommendationControllersRef.current[workoutExerciseId]) {
      recommendationControllersRef.current[workoutExerciseId].abort();
    }

    // Debounce the actual recommendation fetch while surviving unrelated workout rerenders.
    coachRefreshSchedulerRef.current.schedule(timerKey, () => {
      fetchSingleExerciseRecommendation(workoutExerciseId);
    }, 2000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly, workoutStatus, workout?._id, backendService, workout_exercises]);

  // Generate a key for a single exercise based on its state
  const getExerciseStateKey = (exercise) => {
    if (!exercise) return '';
    const completedCount = (exercise.sets || []).filter(s => s && s.complete).length;
    const setWeights = (exercise.sets || []).map(s => s.weight || 0).join(',');
    const setReps = (exercise.sets || []).map(s => s.repetitions || 0).join(',');
    const setDurations = (exercise.sets || []).map(s => s.duration || 0).join(',');
    const setDistances = (exercise.sets || []).map(s => s.distance || 0).join(',');
    const setCalories = (exercise.sets || []).map(s => s.calories || 0).join(',');
    return `${exercise._id}:${completedCount}:${setWeights}:${setReps}:${setDurations}:${setDistances}:${setCalories}`;
  };

  // Refresh individual exercise recommendations when their state changes
  useEffect(() => {
    if (readOnly || workoutStatus !== 'ongoing') return;
    if (!workout_exercises || workout_exercises.length === 0) return;
    
    // Check each exercise for state changes
    workout_exercises.forEach(ex => {
      const currentStateKey = getExerciseStateKey(ex);
      const prevStateKey = exerciseStateKeysRef.current[ex._id];
      
      if (prevStateKey && currentStateKey !== prevStateKey) {
        // A canonical set edit supersedes the member's old task immediately,
        // not only when the debounced next generation begins.
        recommendationControllersRef.current[ex._id]?.abort();
        coachRefreshSchedulerRef.current.schedule(ex._id, () => {
          fetchSingleExerciseRecommendation(ex._id);
        }, 1500);
      }

      // Update the state key
      exerciseStateKeysRef.current[ex._id] = currentStateKey;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly, workout_exercises, workoutStatus]);

  useEffect(() => () => {
    coachRefreshSchedulerRef.current?.dispose();
    exerciseStateKeysRef.current = {};
    Object.values(recommendationControllersRef.current).forEach(controller => controller.abort());
  }, [workout?._id, backendService, readOnly, workoutStatus]);

  // Activities relays the content-free set-review publication signal from the
  // user socket. It wakes only the exact pending member task of this workout.
  useEffect(() => {
    const onPublished = (event) => {
      const { activityId, workoutExerciseId, taskId } = event.detail || {};
      if (typeof activityId !== 'string' || typeof workoutExerciseId !== 'string' || typeof taskId !== 'string') return;
      if (!mountedRef.current || activityId !== workoutScopeRef.current) return;
      if (!Object.prototype.hasOwnProperty.call(reviewPublicationWaitersRef.current, workoutExerciseId)) return;
      const waiter = reviewPublicationWaitersRef.current[workoutExerciseId];
      if (waiter.taskId === null) {
        // The POST has not yet named its task; retain a few exact candidates.
        if (!waiter.early.includes(taskId)) waiter.early = [...waiter.early, taskId].slice(-4);
        return;
      }
      if (taskId !== waiter.taskId) return;
      if (waiter.wake) waiter.wake();
      else waiter.signalled = true;
    };
    window.addEventListener('workout-exercise-review-published', onPublished);
    return () => window.removeEventListener('workout-exercise-review-published', onPublished);
  }, []);

  // ===== Discord Rich Presence =====
  // Only loaded on native builds where the Social SDK plugin exists.
  const [discordSettings, setDiscordSettings] = useState(null);

  useEffect(() => {
    if (readOnly || !DiscordPresenceService.isSupported()) return;
    let cancelled = false;
    (async () => {
      try {
        const settings = await backendService.getDiscordPresenceSettings();
        if (cancelled) return;
        setDiscordSettings(settings);
        // Nothing else brings the client up: the link is held server-side, so it
        // has to be re-established before any presence can be published.
        if (settings?.enabled) await DiscordPresenceService.ensureConnected(backendService);
      } catch (err) {
        console.warn('[DiscordPresence] could not load settings:', err?.message || err);
      }
    })();
    return () => { cancelled = true; };
  }, [backendService, readOnly]);

  useEffect(() => {
    // null means the settings haven't loaded (or can't apply here) — don't touch
    // presence until we know what the user wants.
    if (discordSettings === null) return;

    // Presence tracks the workout, not this component: anything other than an
    // in-progress workout, or opting out, means there is nothing to show.
    if (!discordSettings.enabled || workoutStatus !== 'ongoing') {
      DiscordPresenceService.clearActivity();
      return;
    }

    const current = workout_exercises.find(we => we._id === ongoingExerciseId);
    const meta = current
      ? exercises.find(e => e.id === current.exercise_id || e._id === current.exercise_id)
      : null;
    const sets = current?.sets || [];

    const activity = DiscordPresenceService.buildWorkoutActivity(
      { name: workout.name, started_at: workout.started_at },
      {
        exerciseName: meta?.name,
        completedSets: sets.filter(s => s && s.complete).length,
        totalSets: sets.length
      },
      discordSettings
    );
    if (activity) DiscordPresenceService.setActivity(activity);
  }, [
    discordSettings,
    workoutStatus,
    ongoingExerciseId,
    workout_exercises,
    exercises,
    workout.name,
    workout.started_at
  ]);

  const handleMarkAsRest = async (reason) => {
    try {
      setRestSubmitting(true);
      setRestError(null);
      const response = await backendService.markWorkoutAsRest(workout._id, reason);
      const updated = response?.activity || response;
      if (updated && onActivityRefresh) {
        onActivityRefresh(updated);
      } else if (refreshWorkout) {
        await refreshWorkout();
      }
      setShowRestPrompt(false);
      try { window.dispatchEvent(new Event('regimen:restDays:changed')); } catch (e) {}
    } catch (err) {
      console.error('[Workout.handleMarkAsRest] error:', err);
      const msg = err?.response?.data?.error || err?.message || 'Could not mark as rest day';
      setRestError(msg);
    } finally {
      setRestSubmitting(false);
    }
  };

  const openTravelReplacement = async () => {
    if (!travelReplacementEnabled || travelPreviewLoading) return;
    const requestId = ++travelPreviewRequestRef.current;
    setTravelPreviewLoading(true);
    setTravelError(null);
    setShowRestPrompt(false);
    try {
      const preview = await backendService.previewTravelReplacement(workout._id);
      if (requestId !== travelPreviewRequestRef.current) return;
      if (preview?.eligible === false) {
        setTravelError(formatTravelReplacementReason(preview.reason, preview?.allowance?.scope));
        return;
      }
      modalService.open(TravelReplacementModal, {
        workout,
        preview,
        exercises,
        backendService,
        onCreated: handleTravelReplacementCreated,
      });
    } catch (error) {
      if (requestId !== travelPreviewRequestRef.current) return;
      setTravelError(error?.response?.data?.error || error?.message || 'Could not check travel replacement eligibility.');
    } finally {
      if (requestId === travelPreviewRequestRef.current) {
        setTravelPreviewLoading(false);
      }
    }
  };

  const handleTravelReplacementCreated = () => {
    try { window.dispatchEvent(new Event('regimen:travelReplacements:changed')); } catch (e) {}
    onActivityRefresh?.(null);
  };

  const cancelTravelReplacement = async () => {
    if (travelPreviewLoading) return;
    setTravelPreviewLoading(true);
    setTravelError(null);
    try {
      await backendService.cancelTravelReplacement(workout._id);
      try { window.dispatchEvent(new Event('regimen:travelReplacements:changed')); } catch (e) {}
      onActivityRefresh?.(null);
    } catch (error) {
      setTravelError(error?.response?.data?.error || error?.message || 'Could not cancel the travel replacement.');
    } finally {
      setTravelPreviewLoading(false);
    }
  };

  const handleUnmarkRest = async () => {
    try {
      setRestSubmitting(true);
      setRestError(null);
      const response = await backendService.unmarkWorkoutRest(workout._id);
      const updated = response?.activity || response;
      if (updated && onActivityRefresh) {
        onActivityRefresh(updated);
      } else if (refreshWorkout) {
        await refreshWorkout();
      }
      try { window.dispatchEvent(new Event('regimen:restDays:changed')); } catch (e) {}
    } catch (err) {
      console.error('[Workout.handleUnmarkRest] error:', err);
      const msg = err?.response?.data?.error || err?.message || 'Could not undo rest day';
      setRestError(msg);
    } finally {
      setRestSubmitting(false);
    }
  };

  const handleFinishWorkout = async () => {
    console.log(`[Workout.handleFinishWorkout] Attempting to finish workout ${workout._id}`);
    /*if (videoService && (videoService.isCameraOn() || videoService.isStreamingToBackend)) {
      try {
        console.log('[Workout.handleFinishWorkout] Stopping camera and finalizing video stream...');
        await videoService.stopCamera(); // Stop and finalize video
        console.log('[Workout.handleFinishWorkout] Camera stopped and stream finalization initiated.');
      } catch (error) {
        console.error('[Workout.handleFinishWorkout] Error stopping video stream:', error);
        // Potentially alert the user or decide if workout completion should still proceed
      }
    } else {
        console.log('[Workout.handleFinishWorkout] No active video stream to stop or videoService not available.');
    }*/

    // Proceed to call the original finishActivity prop
    if (finishActivity) {
      finishActivity(workout._id);
    }
  };

  // Gym handling functions
  const handleConfirmGym = async () => {
    if (!gymDetails) return;
    try {
      await backendService.updateWorkoutGym(workout._id, gymDetails._id, true);
      setGymConfirmed(true);
      setShowGymSelector(false);
    } catch (error) {
      console.error('Error confirming gym:', error);
    }
  };

  const handleChangeGym = async (gymId) => {
    try {
      await backendService.updateWorkoutGym(workout._id, gymId, true);
      const selectedGym = userGyms.find(g => g._id === gymId);
      if (selectedGym) {
        setGymDetails({
          _id: selectedGym._id,
          name: selectedGym.name,
          address: selectedGym.address
        });
      }
      setGymConfirmed(true);
      setShowGymSelector(false);
    } catch (error) {
      console.error('Error changing gym:', error);
    }
  };

  const handleClearGym = async () => {
    try {
      await backendService.clearWorkoutGym(workout._id);
      setGymDetails(null);
      setGymConfirmed(false);
      setShowGymSelector(false);
    } catch (error) {
      console.error('Error clearing gym:', error);
    }
  };

  const handleCreateNewGym = async (name, latitude = null, longitude = null) => {
    if (!name.trim()) return;
    
    // If coordinates are provided (e.g. from map pin placement), use them directly
    if (latitude != null && longitude != null) {
      try {
        const newGym = await backendService.createGym({
          name: name.trim(),
          latitude,
          longitude
        });
        setUserGyms(prev => [...prev, newGym]);
        await handleChangeGym(newGym._id);
      } catch (error) {
        console.error('Error creating gym:', error);
      }
      return;
    }
    
    // Otherwise get current location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const newGym = await backendService.createGym({
              name: name.trim(),
              latitude: position.coords.latitude,
              longitude: position.coords.longitude
            });
            setUserGyms(prev => [...prev, newGym]);
            await handleChangeGym(newGym._id);
          } catch (error) {
            console.error('Error creating gym:', error);
          }
        },
        async (error) => {
          // Create gym without location if geolocation fails
          console.warn('Could not get location for new gym:', error);
          try {
            const newGym = await backendService.createGym({
              name: name.trim()
            });
            setUserGyms(prev => [...prev, newGym]);
            await handleChangeGym(newGym._id);
          } catch (err) {
            console.error('Error creating gym:', err);
          }
        }
      );
    } else {
      // Create gym without location if geolocation not available
      try {
        const newGym = await backendService.createGym({
          name: name.trim()
        });
        setUserGyms(prev => [...prev, newGym]);
        await handleChangeGym(newGym._id);
      } catch (error) {
        console.error('Error creating gym:', error);
      }
    }
  };

  // Resolve a search result to one canonical gym and assign it in one mutation
  const handleSelectSearchGym = async (searchGym) => {
    try {
      const { gym } = await backendService.selectWorkoutGymFromSearch(workout._id, {
        osm_id: searchGym.osm_id,
        osm_type: searchGym.osm_type,
        name: searchGym.name,
        latitude: searchGym.latitude,
        longitude: searchGym.longitude,
        address: searchGym.address
      });

      setUserGyms(prev => prev.some(existing => existing._id === gym._id)
        ? prev.map(existing => existing._id === gym._id ? gym : existing)
        : [...prev, gym]);
      setGymDetails({
        _id: gym._id,
        name: gym.name,
        address: gym.address,
        latitude: gym.latitude,
        longitude: gym.longitude
      });
      setGymConfirmed(true);
      setShowGymSelector(false);
      backendService.getGyms()
        .then(setUserGyms)
        .catch(error => console.error('Error reconciling gyms after selection:', error));
    } catch (error) {
      console.error('Error selecting gym:', error);
    }
  };


  // Intentionally do not auto-scroll on exercise selection.
  // Selection changes frequently (e.g. unit toggles, map interactions) and
  // auto-scrolling is disruptive.

  const moveWorkoutExercise = (draggedId, hoveredId) => {
    if (!isExerciseOrderScopeCurrent() || !isWorkoutEditable(workout)) return;
    const rows = workoutExercisesRef.current;
    // Resolve current instances by identity, not indices retained by a hover
    // callback before another move or canonical refresh changed the order.
    const dragIndex = rows.findIndex(exercise => exercise._id === draggedId);
    const hoverIndex = rows.findIndex(exercise => exercise._id === hoveredId);
    if (dragIndex < 0 || hoverIndex < 0 || dragIndex === hoverIndex) return dragIndex;
    const dragged = rows[dragIndex];
    const hovered = rows[hoverIndex];
    const hasGroup = exercise => exercise.superset_group !== null && exercise.superset_group !== undefined;
    if (hasGroup(dragged) && dragged.superset_group === hovered.superset_group) return dragIndex;

    // Both source and destination are atomic blocks. Inserting after just the
    // first member splits the target superset and gives two rendered rows the
    // same React key, creating duplicate/ghost entries on subsequent hovers.
    const moving = hasGroup(dragged)
      ? rows.filter(exercise => exercise.superset_group === dragged.superset_group)
      : [dragged];
    const movingIds = new Set(moving.map(exercise => exercise._id));
    const remaining = rows.filter(exercise => !movingIds.has(exercise._id));
    const targetIndices = remaining.reduce((indices, exercise, index) => {
      if (hasGroup(hovered) ? exercise.superset_group === hovered.superset_group : exercise._id === hoveredId) indices.push(index);
      return indices;
    }, []);
    const targetIndex = dragIndex < hoverIndex
      ? targetIndices[targetIndices.length - 1] + 1
      : targetIndices[0];
    const reordered = [...remaining.slice(0, targetIndex), ...moving, ...remaining.slice(targetIndex)];
    draggingExerciseOrderRef.current = true;
    workoutExercisesRef.current = reordered;
    setWorkoutExerciseState(reordered);
    return reordered.findIndex(exercise => exercise._id === draggedId);
  };

  // Hover updates the ref synchronously; drop must not depend on an effect.

  // Persist the current order to the backend. Called once on drag end.
  // We don't gate on monitor.didDrop() because our drop targets only define
  // `hover` (no `drop` method), which makes didDrop() false on TouchBackend.
  // Local state already reflects the new order optimistically; persist it.
  const persistExerciseOrder = () => {
    if (!isExerciseOrderScopeCurrent() || !isWorkoutEditable(workout)) return;
    const request = {};
    exerciseOrderSaveRef.current = request;
    setExerciseOrderFailure(null);
    const exerciseIds = workoutExercisesRef.current.map(exercise => exercise._id);
    draggingExerciseOrderRef.current = false;
    backendService.reorderExercises(workout._id, exerciseIds)
      .catch(error => {
        console.error('Error reordering workout exercises:', error);
        if (isExerciseOrderScopeCurrent() && exerciseOrderSaveRef.current === request) {
          setExerciseOrderFailure({ scope: exerciseOrderScope });
        }
      });
  };

  const handleUpdateComment = async () => {
    const currentComment = workout.comment || '';
    const newComment = window.prompt("Enter workout comment:", currentComment);
    if (newComment !== null && newComment !== currentComment) {
        try {
            const updated = await backendService.updateWorkout(workout._id, { comment: newComment });
            if (onActivityRefresh) onActivityRefresh(updated);
        } catch(e) {
            console.error(e);
            alert("Failed to update comment");
        }
    }
  };

  const caloriePreviewRequestRef = useRef(null);
  // Earned mirrors the daily-nutrition ledger: completed sets only, preferring
  // the stored per-set estimate. The preview adds unchecked sets so valid
  // entries don't read as 0 kcal before Done; it is display-only, never saved.
  // Sets are read fresh from the server (this component's rows can lag the
  // editor), so any unacknowledged edit withholds the estimate instead of
  // presenting old values as current. Bodyweight is read at the exact workout
  // instant without the legacy day cache, and never guessed on failure.
  const estimateWorkoutCalories = async () => {
    const edits = backendService.workoutSetEditState(workout._id);
    if (edits.pending) return { saving: true };
    const controller = new AbortController();
    let timer;
    const [bodyweight, activity] = await Promise.race([
      Promise.all([
        backendService.fetchBodyweight(workout.created_at || new Date(), { fallback: null, signal: controller.signal, cache: false }),
        backendService.fetchActivity(workout._id, { timeout: 15000, signal: controller.signal }).catch(() => null),
      ]),
      new Promise(resolve => {
        timer = setTimeout(() => { controller.abort(); resolve([null, null]); }, 15000);
      }),
    ]).catch(() => [null, null]).finally(() => clearTimeout(timer));
    const after = backendService.workoutSetEditState(workout._id);
    if (after.pending || after.version !== edits.version) return { saving: true };
    if (bodyweight == null) return { unavailable: 'bodyweight' };
    if (!activity) return { unavailable: 'current sets' };
    let earned = 0, projected = 0;
    for (const exercise of activity.data?.exercises || []) {
      const meta = (exercises || []).find(e => e._id === exercise.exercise_id) || {};
      for (const set of exercise.sets || []) {
        const stored = Number(set.estimated_calories);
        const setCalories = set.complete && Number.isFinite(stored) && stored > 0
          ? stored : estimateSetCalories(set, meta, bodyweight);
        if (set.complete) earned += setCalories;
        projected += setCalories;
      }
    }
    return { earned: Math.round(earned), projected: Math.round(projected) };
  };

  const handleUpdateCalories = async () => {
     if (!isExerciseOrderScopeCurrent() || caloriePreviewRequestRef.current?.scope === exerciseOrderScope) return;
     const request = { scope: exerciseOrderScope };
     caloriePreviewRequestRef.current = request;
     try {
     const hasOverride = workout.calories_override != null;
     const currentOverride = hasOverride ? workout.calories_override : '';
     const estimate = hasOverride ? null : await estimateWorkoutCalories();
     if (!hasOverride && !isExerciseOrderScopeCurrent()) return;
     const { earned, projected, saving, unavailable } = estimate || {};
     const hint = hasOverride
       ? `Current override: ${workout.calories_override} kcal. Clear to use auto-calculated value.`
       : saving
         ? 'Auto-calculated estimate unavailable while set edits are saving. Try again in a moment, or enter a value to override.'
       : unavailable
         ? `Auto-calculated: ${workout.calories || 0} kcal. Estimate for unchecked sets unavailable (${unavailable} didn't load). Enter a value to override.`
         : projected > earned
           ? `Auto-calculated: ${earned} kcal from sets marked Done; ${projected} kcal estimated once every set is Done. Enter a value to override.`
           : `Auto-calculated: ${earned} kcal. Enter a value to override.`;
     const newCalories = window.prompt(hint, currentOverride);
     if (newCalories !== null) {
         const num = newCalories.trim() === '' ? null : parseFloat(newCalories);
         if (num !== null && isNaN(num)) {
             alert("Please enter a valid number");
             return;
         }
         
         try {
            const updated = await backendService.updateWorkout(workout._id, { calories: num });
            if (onActivityRefresh) onActivityRefresh(updated);
         } catch(e) {
             console.error(e);
             alert("Failed to update calories");
         }
     }
     } finally {
       if (caloriePreviewRequestRef.current === request) caloriePreviewRequestRef.current = null;
     }
  };

  const handleAddExercise = () => {
    if (workout.source?.activity_plan_id) {
      if (!window.confirm("Warning: Additional exercises won't count toward consistency. Do you want to continue?")) {
        return;
      }
    }
    modalService.open(ExerciseSelectionModal, {
      exercises,
      onSelect: handleSelectExercise,
      backendService: backendService
    });
  };

  const handleSelectExercise = (exerciseId) => {
    addExercise(exerciseId);
  };

  const addExercise = (exerciseId) => {
    backendService.addExercise(workout._id, exerciseId)
      .then(refreshWorkout)
      .catch(error => console.error(error));
  };

  // =====================
  // Superset Management
  // =====================

  const toggleSupersetSelectionMode = () => {
    if (supersetScope.pending) return;
    if (supersetSelectionMode) {
      // Exiting selection mode - clear selections
      setSelectedForSuperset(new Set());
    }
    setSupersetSelectionMode(!supersetSelectionMode);
  };

  const toggleExerciseForSuperset = (exerciseId) => {
    if (supersetScope.pending) return;
    const exercise = workout_exercises.find(item => item._id === exerciseId);
    if (!exercise || (exercise.status === 'removed' && exercise.superset_group == null)) return;
    // Resolve against the canonical workout, including noncontiguous legacy groups.
    const memberIds = exercise?.superset_group != null
      ? workout_exercises.filter(item => item.superset_group === exercise.superset_group && item.status !== 'removed').map(item => item._id)
      : [exerciseId];
    setSelectedForSuperset(prev => {
      const next = new Set(prev);
      const deselect = memberIds.every(id => next.has(id));
      memberIds.forEach(id => deselect ? next.delete(id) : next.add(id));
      return next;
    });
  };

  // Selection owns the whole card; capture prevents nested detail/mutation actions.
  const supersetSelectionProps = (exerciseId, label, selected) => supersetSelectionMode ? {
    role: 'button',
    tabIndex: 0,
    'aria-label': `${label}${selected ? `. Selection order ${supersetSelectionOrder.get(exerciseId)}` : ''}`,
    'aria-pressed': selected,
    onClickCapture: event => {
      event.preventDefault();
      event.stopPropagation();
      toggleExerciseForSuperset(exerciseId);
    },
    onDoubleClickCapture: event => {
      event.preventDefault();
      event.stopPropagation();
    },
    onKeyDownCapture: event => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      event.stopPropagation();
      if (event.target === event.currentTarget && !event.repeat) toggleExerciseForSuperset(exerciseId);
    },
  } : {};

  const createSupersetFromSelection = async () => {
    if (!supersetScope.active || supersetScope.pending) return;
    if (selectedForSuperset.size < 2) {
      alert('Please select at least 2 exercises to create a superset');
      return;
    }
    
    supersetScope.pending = true;
    setSupersetSaving(true);
    try {
      // Set iteration preserves selection chronology, not workout list order.
      const exerciseIds = [...selectedForSuperset];
      
      await backendService.createSuperset(workout._id, exerciseIds);
      if (!supersetScope.active) return;
      
      // Clear selection and exit mode
      setSelectedForSuperset(new Set());
      setSupersetSelectionMode(false);
      
      // Refresh to show the updated superset grouping
      await refreshWorkout();
    } catch (error) {
      if (!supersetScope.active) return;
      console.error('Error creating superset:', error);
      alert('Failed to create superset: ' + (error.response?.data?.error || error.message));
    } finally {
      supersetScope.pending = false;
      if (supersetScope.active) setSupersetSaving(false);
    }
  };

  const handleDissolveSuperset = async (supersetGroup) => {
    if (!window.confirm('Are you sure you want to unlink all exercises in this superset?')) {
      return;
    }
    
    try {
      await backendService.dissolveSuperset(workout._id, supersetGroup);
      await refreshWorkout();
    } catch (error) {
      console.error('Error dissolving superset:', error);
      alert('Failed to dissolve superset: ' + (error.response?.data?.error || error.message));
    }
  };

  // =====================
  // Reorder Mode
  // =====================

  const toggleReorderMode = () => {
    const enteringReorderMode = !reorderMode;
    setReorderMode(enteringReorderMode);
    // Exit superset selection mode if entering reorder mode
    if (enteringReorderMode && supersetSelectionMode) {
      setSupersetSelectionMode(false);
      setSelectedForSuperset(new Set());
    }
  };

  const regenerateWorkout = async (userInput) => {
    setIsRefreshing(true);
    try {
      // Backend returns 202 immediately and runs generation in the background.
      // Notification can arrive via either the WebSocket activity_updated
      // event (handled in Activities.js) or via the polling effect below
      // — whichever fires first.
      await backendService.regenerateWorkout(workout._id, userInput);
    } catch (error) {
      console.error('Error starting workout regeneration:', error);
      window.alert(`Error regenerating workout: ${error.response ? error.response.data.error : error.message}`);
      setIsRefreshing(false);
    }
  };

  // While a regeneration is in flight, poll the workout periodically so the
  // UI updates even when this component is rendered outside Activities.js
  // (e.g. inside PlanItem's plan editor, which doesn't share the WS-driven
  // activity refresh path).
  useEffect(() => {
    if (!isRefreshing) return undefined;
    let cancelled = false;
    const baselineSig = JSON.stringify((workout?.data?.exercises || []).map(e => e._id));
    const poll = async () => {
      if (!isForeground()) return;
      try {
        const fresh = await backendService.fetchActivity(workout._id);
        if (cancelled) return;
        const freshSig = JSON.stringify((fresh?.data?.exercises || []).map(e => e._id));
        if (freshSig !== baselineSig) {
          if (onActivityRefresh) onActivityRefresh(fresh);
          // Pull fresh state into local Workout state too, regardless of
          // whether parent forwards the new prop back to us.
          setWorkoutExercises(fresh.data?.exercises || []);
          if (fresh.name) onWorkoutNameChange(fresh.name);
          setWorkoutStatus(fresh.status);
          setIsRefreshing(false);
        }
      } catch (err) {
        // Network blips while polling are non-fatal — keep trying.
      }
    };
    const intervalId = setInterval(poll, 3000);
    // Catch up right away if the regeneration finished while we were away.
    const unsubscribe = subscribeForeground((active) => { if (active) poll(); });
    return () => { cancelled = true; clearInterval(intervalId); unsubscribe(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshing]);

  // Clear the refreshing spinner once the workout's exercises have been
  // replaced by the background regeneration (signalled via WebSocket).
  const prevExerciseSignatureRef = useRef(null);
  useEffect(() => {
    const propExercises = workout?.data?.exercises || [];
    const sig = JSON.stringify(propExercises.map(we => we._id));

    // When the activity prop's exercise list changes (e.g. via WebSocket
    // activity_updated after a background regeneration), pull the new data
    // into local state. Workout normally only re-fetches on _id/refreshKey
    // changes, so without this the UI would never reflect the new exercises.
    if (prevExerciseSignatureRef.current !== null && prevExerciseSignatureRef.current !== sig) {
      refreshWorkout();
      if (isRefreshing) setIsRefreshing(false);
    }
    prevExerciseSignatureRef.current = sig;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workout?.data?.exercises]);

  const openRegenerateModal = () => {
    if (workout.source?.activity_plan_id) {
      if (!window.confirm("Warning: Regenerating the workout may affect your consistency. Do you want to continue?")) {
        return;
      }
    }
    modalService.open(RegenerateWorkoutModal, {
      workoutId: workout._id,
      backendService: backendService,
      onSelect: regenerateWorkout
    });
  };

  const getRemovedExercises = () => {
    if (!workout.source?.activity_snapshot?.data?.exercises) {
      return [];
    }

    const originalExercises = workout.source.activity_snapshot.data.exercises;
    const currentExercises = workout_exercises;

    const originalCount = originalExercises.reduce((acc, exercise) => {
      acc[exercise.exercise_id] = (acc[exercise.exercise_id] || 0) + 1;
      return acc;
    }, {});

    const currentCount = currentExercises.reduce((acc, exercise) => {
      acc[exercise.exercise_id] = (acc[exercise.exercise_id] || 0) + 1;
      return acc;
    }, {});

    const removedExercises = [];
    for (const [exerciseId, count] of Object.entries(originalCount)) {
      const currentCountForId = currentCount[exerciseId] || 0;
      if (currentCountForId < count) {
        const removedCount = count - currentCountForId;
        for (let i = 0; i < removedCount; i++) {
          const removedExercise = originalExercises.find((ex) => ex.exercise_id === exerciseId);
          if (removedExercise) {
            // push a shallow copy marked as removed so we don't mutate the original
            removedExercises.push({ ...removedExercise, status: 'removed' });
          }
        }
      }
    }

    return removedExercises;
  };

  const removedExercises = getRemovedExercises();
  const showCompletedCalories = !workout.is_template && workoutStatus === 'complete';
  const buttonCalories = workout.calories_override ?? workout.calories;
  const completedCalories = partDateFilter && workout._partCalories != null
    ? Math.round(workout._partCalories)
    : Number.isFinite(workout.calories) ? Math.round(workout.calories) : null;

  /*useEffect(() => {
    // If workout is ongoing and videoService is available, ensure streaming resumes
    if (
      workoutStatus === 'ongoing' &&
      videoService &&
      !videoService.isCameraOn()
    ) {
      // Resume camera and streaming to backend
      (async () => {
        try {
          console.log('[Workout] Resuming camera and backend stream after refresh...');
          await videoService.startCamera({ streamToBackend: true });
          console.log('[Workout] Camera and backend stream resumed.');
        } catch (err) {
          console.error('[Workout] Failed to resume camera/stream after refresh:', err);
        }
      })();
    }
    // Optionally, if camera is on but not streaming, ensure backend stream starts
    if (
      workoutStatus === 'ongoing' &&
      videoService &&
      videoService.isCameraOn() &&
      !videoService.isStreamingToBackend
    ) {
      (async () => {
        try {
          console.log('[Workout] Camera on but backend stream not active, starting backend stream...');
          await videoService.startCamera({ streamToBackend: true });
          console.log('[Workout] Backend stream started.');
        } catch (err) {
          console.error('[Workout] Failed to start backend stream after refresh:', err);
        }
      })();
    }
  }, [workoutStatus, videoService]);*/

  const originalExercises = workout.source?.activity_snapshot?.data?.exercises || [];
  const originalCount = originalExercises.reduce((acc, exercise) => {
    acc[exercise.exercise_id] = (acc[exercise.exercise_id] || 0) + 1;
    return acc;
  }, {});

  const currentCount = workout_exercises.reduce((acc, exercise) => {
    if (exercise.status !== 'removed') {
      acc[exercise.exercise_id] = (acc[exercise.exercise_id] || 0) + 1;
    }
    return acc;
  }, {});
  const showWorkoutCoach = shouldShowWorkoutCoachDock({
    readOnly,
    workoutStatus,
    isTemplate: Boolean(workout.is_template),
  });
  const workoutProposalAnnotations = useMemo(() => buildWorkoutProposalAnnotations({
    workout: { ...workout, status: workoutStatus, data: { ...(workout.data || {}), exercises: workout_exercises } },
    proposals: strategyProposals
  }), [strategyProposals, workout, workoutStatus, workout_exercises]);
  const approveAnnotatedWorkoutProposal = async proposalId => {
    if (!proposalId || busyStrategyProposalRef.current) return;
    busyStrategyProposalRef.current = String(proposalId);
    setBusyStrategyProposalId(String(proposalId));
    setStrategyProposalError('');
    try {
      await backendService.approveStrategyProposal(proposalId);
      if (!strategyProposalScope.active) return;
      // A pre-approval read must not restore the just-approved proposal.
      strategyProposalReadRef.current = null;
      setStrategyProposals(previous => previous.filter(proposal => String(proposal._id || proposal.id) !== String(proposalId)));
      await refreshWorkout();
      await loadStrategyProposals({ fresh: true });
    } catch (proposalError) {
      if (!strategyProposalScope.active) return;
      setStrategyProposalError(proposalError?.message || 'Could not approve these workout changes.');
      await loadStrategyProposals({ fresh: true });
    } finally {
      busyStrategyProposalRef.current = null;
      setBusyStrategyProposalId(null);
    }
  };
  const workoutCoachRefreshVersion = useMemo(() => [
    refreshKey,
    recommendationVersion,
    workoutCoachThreadRefreshNonce,
    workoutStatus,
    ...workout_exercises.map(item => `${item._id}:${(item.sets || []).filter(set => set.complete).length}:${(item.conversation_log || []).length}`),
  ].join('|'), [refreshKey, recommendationVersion, workoutCoachThreadRefreshNonce, workoutStatus, workout_exercises]);

  return (
    <>

        {/* TODO: Revisit map feature later
        {!workout.is_template && ((workoutStatus === 'ongoing') || (workoutStatus === 'complete' && completeSetsWithCoords.length > 0)) && (
          <SetMap
            sets={setsWithCoords}
            forceVisible={workoutStatus === 'ongoing'}
            showUserLocation={workoutStatus === 'ongoing'}
            userAvatarUrl={backendService?.getUser?.()?.avatar_url || null}
            selectedExerciseId={ongoingExerciseId}
            onPathClick={(exerciseId) => {
              try {
                selectionLog('setmap onPathClick', {
                  workoutId: workout?._id,
                  exerciseId,
                });
                // set the ongoing exercise in the UI
                setOngoingExerciseId(exerciseId);

                // User-triggered selection: scroll the row into view and collapse the top bar.
                requestAnimationFrame(() => scrollWorkoutExerciseIntoView(exerciseId));
              } catch (e) {
                console.error('Error handling path click:', e);
              }
            }}
          />
        )}
        */}

        <div key={workout._id} className="workout-section" data-workout-id={workout._id}>
          <>
            {workoutStatus === 'rest' && (
              <div className={`workout-rest-banner ${workout?.data?.rest_reason === 'sick' ? 'rest-sick' : 'rest-fine'}`}>
                <div className="workout-rest-banner-text">
                  <div className="workout-rest-banner-title">
                    {workout?.data?.rest_reason === 'sick' ? 'Rest day — recovering' : 'Rest day — taking a break'}
                  </div>
                  <div className="workout-rest-banner-subtitle">
                    Excluded from your consistency calculation.
                  </div>
                </div>
                {!readOnly && (
                  <button
                    className="workout-rest-banner-undo"
                    onClick={handleUnmarkRest}
                    disabled={restSubmitting}
                  >
                    {restSubmitting ? 'Undoing…' : 'Undo'}
                  </button>
                )}
                {restError && (
                  <div className="workout-rest-banner-error">{restError}</div>
                )}
              </div>
            )}
            {hasTravelReplacement && (
              <div className="workout-travel-banner">
                <div>
                  <strong>Travel replacement assigned</strong>
                  <div>This workout is linked to the replacement activity carrying its consistency obligation.</div>
                </div>
              </div>
            )}
            {isExistingActivityReplacementEvidence && (
              <div className="workout-travel-banner replacement">
                <div className="workout-travel-copy">
                  <strong>Used for travel replacement</strong>
                  <div>
                    Preserved consistency credit for {workout.travel_replacement_assignment?.original_activity_name || 'a planned workout'}.
                    This completion record is locked so the replacement remains valid.
                  </div>
                </div>
              </div>
            )}
            {isTravelReplacement && (
              <div className="workout-travel-banner replacement">
                <div className="workout-travel-copy">
                  <strong>{isTrackedActivityReplacement ? 'Travel Replacement Activity' : 'Travel Replacement Workout'}</strong>
                  <div>
                    Preserves consistency credit for {replacedWorkoutName} when completed by its deadline; it does not reproduce the original training effect.
                  </div>
                </div>
                {onViewReplacedWorkout && (
                  <button
                    type="button"
                    className="workout-travel-original-link"
                    onClick={onViewReplacedWorkout}
                    aria-label={`View original workout: ${replacedWorkoutName}`}
                  >
                    <span className="workout-travel-original-state">Original workout — not performed</span>
                    <span className="workout-travel-original-name">{replacedWorkoutName}</span>
                    <span className="workout-travel-original-action">View original workout <span aria-hidden="true">›</span></span>
                  </button>
                )}
                {!readOnly && workoutStatus === 'pending' && (
                  <button className="workout-travel-cancel" type="button" onClick={cancelTravelReplacement} disabled={travelPreviewLoading}>
                    {travelPreviewLoading ? 'Canceling…' : 'Cancel replacement'}
                  </button>
                )}
                {travelError && <div className="rest-prompt-error" role="alert">{travelError}</div>}
              </div>
            )}
            {workout.data?.live_stream && (
              <WorkoutStreamViewer
                activityId={workout._id}
                stream={workout.data.live_stream}
                backendService={backendService}
              />
            )}
            {isRefreshing ? (
              <div className="spinner workout-refresh-spinner"></div>
            ) : (
              <>
                {/* Gym indicator - show if gym is inferred, confirmed, or user has location for search */}
                {!readOnly && !workout.is_template && (workoutStatus === 'ongoing' || workoutStatus === 'complete') && !isExistingActivityReplacementEvidence && (gymDetails || showGymSelector || userLocation) && (
                  <div className={`workout-gym-bar ${!gymConfirmed ? 'unconfirmed' : ''}`}>
                    <div className="gym-info" onClick={() => setShowGymSelector(!showGymSelector)}>
                      <MapPinIcon style={{ width: 16, height: 16, marginRight: 6 }} />
                      {gymDetails ? (
                        <>
                          <span className="gym-name">{gymDetails.name}</span>
                          {!gymConfirmed && <span className="gym-unconfirmed-label">(tap to confirm)</span>}
                        </>
                      ) : (
                        <span className="gym-name gym-prompt">
                          {workoutStatus === 'complete' ? 'Where were you working out?' : 'Where are you working out?'}
                        </span>
                      )}
                      <ChevronDownIcon style={{ width: 14, height: 14, marginLeft: 'auto' }} />
                    </div>
                    
                    {showGymSelector && (
                      <GymSelector
                        gymDetails={gymDetails}
                        gymConfirmed={gymConfirmed}
                        userGyms={userGyms}
                        userLocation={userLocation}
                        backendService={backendService}
                        onConfirmGym={handleConfirmGym}
                        onChangeGym={handleChangeGym}
                        onClearGym={handleClearGym}
                        onCreateNewGym={handleCreateNewGym}
                        onSelectSearchGym={handleSelectSearchGym}
                      />
                    )}
                  </div>
                )}
                
                {exerciseOrderFailure?.scope === exerciseOrderScope && (
                  <div role="alert">Exercise order could not be saved. Try reordering again.</div>
                )}
                <div ref={exerciseListRef} className={`workout-exercise-list ${reorderMode ? 'reorder-mode-active' : ''}`}>
                  {/* Reorder/Superset controls at top. While selecting, the actions
                      float at the bottom instead so the list stays unobstructed. */}
                  {workout_exercises.length >= 2 && !isTravelReplacement && workoutStatus !== 'completed' && isWorkoutEditable(workout) && !reorderMode && !supersetSelectionMode && (
                    <div className="reorder-toggle-row">
                      <button className="reorder-toggle-link" onClick={toggleSupersetSelectionMode}>
                        <LinkIcon style={{ width: 14, height: 14 }} />
                        Create Superset
                      </button>
                      <button className="reorder-toggle-link" onClick={toggleReorderMode}>
                        <Bars3Icon style={{ width: 14, height: 14 }} />
                        Reorder
                      </button>
                    </div>
                  )}
                  
                  {/* Reorder Mode - Show compact draggable items */}
                  {reorderMode && (
                    <>
                      <CustomDragLayer />
                      {groupedExercises.map((group, groupIndex) => {
                    if (group.type === 'superset') {
                      const workoutExercisesInSuperset = group.exercises.map(e => e.exercise);
                      const firstExercise = workoutExercisesInSuperset[0];
                      const supersetExerciseNames = workoutExercisesInSuperset.map(we => {
                        const details = exercises.find(e => e.id === we.exercise_id || e._id === we.exercise_id);
                        return { name: details?.name || 'Exercise' };
                      });
                      
                      return (
                        <DraggableCompactExercise
                          key={workoutExerciseScopeKey(workout._id, `superset:${group.supersetGroup}`)}
                          workout_exercise={firstExercise}
                          index={group.exercises[0]?.index || 0}
                          moveWorkoutExercise={moveWorkoutExercise}
                          onDragEnd={persistExerciseOrder}
                          isSuperset={true}
                          supersetExercises={supersetExerciseNames}
                        />
                      );
                    } else {
                      const { exercise: workout_exercise, index } = group.exercises[0];
                      const exerciseDetails = exercises.find(e => e.id === workout_exercise.exercise_id || e._id === workout_exercise.exercise_id);
                      
                      return (
                        <DraggableCompactExercise
                          key={workoutExerciseScopeKey(workout._id, workout_exercise._id)}
                          workout_exercise={workout_exercise}
                          index={index}
                          moveWorkoutExercise={moveWorkoutExercise}
                          onDragEnd={persistExerciseOrder}
                          exerciseName={exerciseDetails?.name || 'Exercise'}
                          setCount={workout_exercise.sets?.length || 0}
                        />
                      );
                    }
                  })}
                    </>
                  )}
                  
                  {/* Normal Mode - Render grouped exercises */}
                  {!readOnly && workoutStatus === 'ongoing' && Object.keys(exerciseRecommendations).length === 0
                    && ['pending', 'checking', 'failed', 'unavailable', 'paused', 'not-started'].includes(recommendationJob.status) && (
                    <div className="workout-guidance-status" role="status">
                      <span>{['pending', 'checking'].includes(recommendationJob.status)
                        ? 'Preparing exercise guidance…' : 'Exercise guidance is unavailable. You can keep logging sets.'}</span>
                      {!['pending', 'checking'].includes(recommendationJob.status) && (
                        <button type="button" onClick={() => recommendationJob.refresh()}>Check guidance</button>
                      )}
                      {recommendationJob.canRetry && (
                        <button type="button" onClick={() => recommendationJob.retry()}>Retry guidance</button>
                      )}
                    </div>
                  )}
                  {strategyProposalReadError && (
                    <div className="workout-proposal-error" role="alert">
                      <span>{strategyProposalReadError}</span>
                      <button type="button" className="workout-proposal-retry" onClick={loadStrategyProposals} disabled={strategyProposalsLoading}>
                        {strategyProposalsLoading ? 'Retrying…' : 'Retry'}
                      </button>
                    </div>
                  )}
                  {strategyProposalError && <div className="workout-proposal-error" role="alert">{strategyProposalError}</div>}
                  {!reorderMode && groupedExercises.map((group, groupIndex) => {
                    if (group.type === 'superset') {
                      // Render superset group with zipped sets view
                      const workoutExercisesInSuperset = group.exercises.map(e => e.exercise);
                      const selectableMembers = workoutExercisesInSuperset.filter(exercise => exercise.status !== 'removed');
                      const isAnyOngoing = group.exercises.some(e => e.exercise._id === ongoingExerciseId);
                      
                      // When clicking the superset, select the first exercise in it
                      const handleSupersetClick = () => {
                        if (supersetSelectionMode) {
                          toggleExerciseForSuperset(workoutExercisesInSuperset[0]._id);
                        } else if (workoutExercisesInSuperset.length > 0 && setOngoingExerciseId) {
                          setOngoingExerciseId(workoutExercisesInSuperset[0]._id);
                        }
                      };
                      
                      return (
                        <div 
                          key={workoutExerciseScopeKey(workout._id, `superset:${group.supersetGroup}:${workoutExercisesInSuperset[0]._id}`)}
                          className={`superset-group-wrapper ${isAnyOngoing ? 'active' : ''} ${supersetSelectionMode && selectableMembers.length > 0 && selectableMembers.every(exercise => selectedForSuperset.has(exercise._id)) ? 'superset-selected' : ''}`}
                          tabIndex={-1}
                          ref={element => workoutExercisesInSuperset.forEach(ex => {
                            if (element) navigationTargetsRef.current.set(ex._id, element);
                            else navigationTargetsRef.current.delete(ex._id);
                          })}
                          onClick={supersetSelectionMode ? undefined : handleSupersetClick}
                          {...supersetSelectionProps(
                            selectableMembers[0]?._id,
                            `Select superset: ${workoutExercisesInSuperset.map(we => exercises.find(ex => ex._id === we.exercise_id)?.name || 'Exercise').join(', ')}`,
                            selectableMembers.length > 0 && selectableMembers.every(exercise => selectedForSuperset.has(exercise._id))
                          )}
                        >
                          <div className="superset-group-header" onClick={(e) => e.stopPropagation()}>
                            <div className="superset-label-inline">
                              <LinkIcon style={{ width: 14, height: 14 }} />
                              SUPERSET
                            </div>
                            {isWorkoutEditable(workout) && workoutStatus !== 'completed' && !isTravelReplacement && !supersetSelectionMode && (
                              <button 
                                className="superset-dissolve-btn-inline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDissolveSuperset(group.supersetGroup);
                                }}
                                title="Unlink superset"
                              >
                                <XMarkIcon style={{ width: 14, height: 14 }} />
                                Unlink
                              </button>
                            )}
                          </div>
                          <SupersetExercise
                            navigationSelection={navigationSelection}
                            workout={{...workout, status: workoutStatus}}
                            workoutExercises={workoutExercisesInSuperset}
                            exercises={exercises}
                            backendService={backendService}
                            modalService={modalService}
                            refreshWorkout={refreshWorkout}
                            onCoachSuggestionResolved={reconcileResolvedCoachSuggestion}
                            isOngoing={isAnyOngoing}
                            setOngoingExerciseId={supersetSelectionMode ? undefined : setOngoingExerciseId}
                            isSelectionMode={supersetSelectionMode}
                            selectionOrder={supersetSelectionMode ? supersetSelectionOrder : undefined}
                            isTemplateMode={workout.is_template}
                            allowCompletion={!readOnly && (workout.is_template || workoutStatus === 'ongoing')}
                            readOnly={readOnly}
                            structureLocked={isTravelReplacement}
                            recommendations={workoutExercisesInSuperset.reduce((acc, we) => {
                              if (exerciseRecommendations[we._id]) {
                                acc[we._id] = exerciseRecommendations[we._id];
                              }
                              return acc;
                            }, {})}
                            isLoadingRecommendations={isLoadingRecommendations}
                            updatingExerciseIds={updatingExerciseIds}
                            updatedExerciseIds={updatedExerciseIds}
                            onSubmitMessage={handleSubmitMessage}
                            onAnswerQuestion={handleAnswerQuestion}
                            onCompletedSetFieldChanged={handleCompletedSetFieldChanged}
                            setDateFilter={partDateFilter}
                            coachImageUrl={coachImageUrl}
                            coachEmoji={coachEmoji}
                            showInlineCoach={false}
                            proposalAnnotationsByExercise={workoutProposalAnnotations}
                            onApproveProposal={approveAnnotatedWorkoutProposal}
                            busyStrategyProposalId={busyStrategyProposalId}
                            coachAddedSetEffects={coachAddedSetEffects}
                            onCoachAddedSetDisplayed={onCoachAddedSetDisplayed}
                            coachFieldEffects={coachFieldEffects}
                            onCoachFieldEffectDisplayed={markCoachFieldEffectDisplayed}
                          />
                        </div>
                      );
                    } else {
                      // Render single exercise
                      const { exercise: workout_exercise, index } = group.exercises[0];
                      const scopedExerciseKey = workoutExerciseScopeKey(workout._id, workout_exercise._id);
                      
                      return (
                        <div 
                          key={scopedExerciseKey}
                          className={`single-exercise-wrapper ${supersetSelectionMode && selectedForSuperset.has(workout_exercise._id) ? 'superset-selected' : ''}`}
                          tabIndex={-1}
                          ref={element => {
                            if (element) navigationTargetsRef.current.set(workout_exercise._id, element);
                            else navigationTargetsRef.current.delete(workout_exercise._id);
                          }}
                          {...supersetSelectionProps(
                            workout_exercise._id,
                            `Select exercise: ${exercises.find(ex => ex._id === workout_exercise.exercise_id)?.name || 'Exercise'}`,
                            selectedForSuperset.has(workout_exercise._id)
                          )}
                        >
                          <DraggableWorkoutExercise
                            key={scopedExerciseKey}
                            index={index}
                            workout={{...workout, status: workoutStatus}}
                            workout_exercise={workout_exercise}
                            moveWorkoutExercise={moveWorkoutExercise}
                            onDragEnd={persistExerciseOrder}
                            exercises={exercises}
                            backendService={backendService}
                            modalService={modalService}
                            refreshWorkout={refreshWorkout}
                            onCoachSuggestionResolved={reconcileResolvedCoachSuggestion}
                            isOngoing={workout_exercise._id === ongoingExerciseId}
                            setOngoingExerciseId={supersetSelectionMode ? undefined : setOngoingExerciseId}
                            isSelectionMode={supersetSelectionMode}
                            selectionOrder={supersetSelectionMode ? supersetSelectionOrder.get(workout_exercise._id) : undefined}
                            isComplianceCritical={(currentCount[workout_exercise.exercise_id] || 0) <= (originalCount[workout_exercise.exercise_id] || 0)}
                            recommendation={exerciseRecommendations[workout_exercise._id]}
                            isLoadingRecommendation={isLoadingRecommendations && !exerciseRecommendations[workout_exercise._id]}
                            isUpdatingRecommendation={updatingExerciseIds.has(workout_exercise._id) || updatedExerciseIds.has(workout_exercise._id)}
                            onSubmitMessage={handleSubmitMessage}
                            onAnswerQuestion={handleAnswerQuestion}
                            onCompletedSetFieldChanged={handleCompletedSetFieldChanged}
                            readOnly={readOnly}
                            structureLocked={isTravelReplacement}
                            allowValueEditingWhenStructureLocked={isTrackedActivityReplacement}
                            setDateFilter={partDateFilter}
                            coachImageUrl={coachImageUrl}
                            coachEmoji={coachEmoji}
                            showInlineCoach={false}
                            proposalAnnotations={workoutProposalAnnotations[String(workout_exercise._id)] || []}
                            onApproveProposal={approveAnnotatedWorkoutProposal}
                            busyStrategyProposalId={busyStrategyProposalId}
                            coachAddedSetEffects={coachAddedSetEffects}
                            onCoachAddedSetDisplayed={onCoachAddedSetDisplayed}
                            coachFieldEffects={coachFieldEffects}
                            onCoachFieldEffectDisplayed={markCoachFieldEffectDisplayed}
                          />
                        </div>
                      );
                    }
                  })}

                  {removedExercises.map((removed_exercise) => (
                    <div key={removed_exercise._id} className="workout-exercise-item">
                        <WorkoutExercise
                          key={removed_exercise._id}
                          workout={workout}
                          workout_exercise={removed_exercise}
                          exercises={exercises}
                          editable={false}
                          isTemplateMode={false}
                          backendService={backendService}
                          modalService={modalService}
                          refreshWorkout={refreshWorkout}
                          isOngoing={false}
                          allowCompletion={false}
                          readOnly={readOnly}
                        >
                        </WorkoutExercise>
                    </div>
                  ))}
              </div>
              </>
            )}
            
            {workout.comment && (
              <div className="workout-comment-display">
                "{workout.comment}"
              </div>
            )}

            {showCompletedCalories && (
              <div className="workout-completed-calories" title="Calories contributing to your daily nutrition budget">
                <FireIcon style={{ width: 16, height: 16 }} />
                <span>
                  Calories burned: {completedCalories !== null ? `${completedCalories} kcal` : 'Unavailable'}
                  {workout.calories_override != null ? ' (manual)' : ''}
                  {totalParts > 1 && ` · Part ${partNumber} of ${totalParts}`}
                </span>
              </div>
            )}

            {!reorderMode && isWorkoutEditable(workout) && (
              <>
                 {!isRefreshing && !workout.is_template && (
                  <div className="workout-aux-actions">
                     <button className="btn-aux" onClick={handleUpdateComment}>
                        <ChatBubbleBottomCenterTextIcon style={{ width: 18, height: 18 }} />
                        {workout.comment ? 'Edit Comment' : 'Add Comment'}
                     </button>
                     <button className="btn-aux" onClick={handleUpdateCalories}>
                        <FireIcon style={{ width: 18, height: 18 }} />
                        {Number.isFinite(buttonCalories) ? `${Math.round(buttonCalories)} kcal${workout.calories_override != null ? ' ✎' : ''}` : 'Calories'}
                     </button>
                  </div>
                )}
                {!isTravelReplacement && (
                  <div className="center-button pad-button-up-big workout-action-buttons">
                    <button className="btn-add" onClick={(e) => handleAddExercise()} disabled={isRefreshing || supersetSelectionMode || reorderMode}>
                      <PlusIcon style={{ width: 20, height: 20, marginRight: 8 }} />
                      Add Exercise
                    </button>
                  </div>
                )}
                {!isTravelReplacement && !workout.source?.activity_plan_id && (
                  <div className="center-button pad-button-up-small">
                    <button className="btn-select" onClick={openRegenerateModal} disabled={isRefreshing || supersetSelectionMode || reorderMode}>
                      <ArrowPathIcon style={{ width: 20, height: 20, marginRight: 8 }} />
                      Regenerate Workout
                    </button>
                  </div>
                )}
                {!workout.is_template && !isTravelReplacement && isDayActionEligible && (
                  <div className="center-button pad-button-up-small workout-rest-action">
                    {travelReplacementEnabled && (
                      <button
                        className="btn-travel-replacement"
                        type="button"
                        onClick={openTravelReplacement}
                        disabled={isRefreshing || supersetSelectionMode || reorderMode || restSubmitting || travelPreviewLoading}
                        title="Replace this unexpired workout with a guided circuit or a tracked cardio activity"
                      >
                        {travelPreviewLoading ? 'Checking…' : '🧳 Use Travel Replacement'}
                      </button>
                    )}
                    {travelError && <div className="rest-prompt-error" role="alert">{travelError}</div>}
                    {!showRestPrompt ? (
                      <button
                        className="btn-rest-day"
                        onClick={() => { setRestError(null); setShowRestPrompt(true); }}
                        disabled={isRefreshing || supersetSelectionMode || reorderMode || restSubmitting}
                        title="Mark this workout as a rest day so it doesn't affect your consistency"
                      >
                        🌴 Mark as Rest Day
                      </button>
                    ) : (
                      <div className="rest-prompt">
                        <div className="rest-prompt-title">How are you feeling today?</div>
                        <div className="rest-prompt-options">
                          <button
                            className="rest-prompt-option rest-fine"
                            onClick={() => handleMarkAsRest('fine')}
                            disabled={restSubmitting}
                          >
                            <span className="rest-prompt-icon">🌴</span>
                            <span className="rest-prompt-label">Feeling fine</span>
                            <span className="rest-prompt-sub">Just taking a break</span>
                          </button>
                          <button
                            className="rest-prompt-option rest-sick"
                            onClick={() => handleMarkAsRest('sick')}
                            disabled={restSubmitting}
                          >
                            <span className="rest-prompt-icon">🛌</span>
                            <span className="rest-prompt-label">Sick / recovering</span>
                            <span className="rest-prompt-sub">Need to rest up</span>
                          </button>
                        </div>
                        <div className="rest-prompt-cancel">
                          <button
                            className="btn-link"
                            onClick={() => { setShowRestPrompt(false); setRestError(null); }}
                            disabled={restSubmitting}
                          >
                            Cancel
                          </button>
                        </div>
                        {restError && (
                          <div className="rest-prompt-error">{restError}</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
            {!reorderMode && ((isWorkoutEditable(workout) && workout.status === "ongoing") || completion.pending) && !workout.is_template && (
              <div className="center-button pad-button-up-small">
                <SubmitFeedbackButton
                  className="btn-select btn-submit-workout"
                  label="Submit Workout"
                  icon={<CheckIcon />}
                  feedback={completion}
                  noun={SUBMIT_NOUNS.workout}
                  onClick={handleFinishWorkout}
                />
              </div>
            )}
            <SubmitFeedbackAnnouncement feedback={completion} noun={SUBMIT_NOUNS.workout} />
            <div className="superset-selection-announcement" role="status" aria-label="Superset selection order" aria-live="polite" aria-atomic="true">
              {supersetSelectionMode && [...selectedForSuperset].map((id, index) => {
                const instance = workout_exercises.find(exercise => exercise._id === id);
                return `${index + 1}. ${exercises.find(exercise => exercise._id === instance?.exercise_id)?.name || 'Exercise'}`;
              }).join(', ')}
            </div>
            {reorderMode && (
              <FloatingSelectionActions
                label="Move exercises actions"
                confirmLabel="Done reordering"
                onConfirm={toggleReorderMode}
              />
            )}
            {supersetSelectionMode && (
              <FloatingSelectionActions
                label="Superset selection actions"
                busy={supersetSaving}
                confirmLabel={`Link ${selectedForSuperset.size}`}
                confirmIcon={LinkIcon}
                onConfirm={createSupersetFromSelection}
                disabled={selectedForSuperset.size < 2}
                onCancel={toggleSupersetSelectionMode}
              />
            )}
            {!reorderMode && !isTravelReplacement && showWorkoutCoach && (
              <WorkoutCoachDock
                workout={{ ...workout, status: workoutStatus }}
                workoutExercises={workout_exercises}
                exercises={exercises}
                selectedExerciseId={ongoingExerciseId}
                backendService={backendService}
                onSubmitMessage={handleSubmitMessage}
                onAnswerQuestion={handleAnswerQuestion}
                onCoachSuggestionResolved={reconcileResolvedCoachSuggestion}
                refreshWorkout={refreshWorkout}
                recommendations={exerciseRecommendations}
                coachImageUrl={coachImageUrl}
                coachEmoji={coachEmoji}
                coachIdentitySettled={coachIdentitySettled}
                refreshVersion={workoutCoachRefreshVersion}
                onGenerationSettled={() => recommendationJob.reconcile()}
              />
            )}
          </>
        </div>
    </>
  );
}

export default Workout;
